import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { TLCode, Page } from '../../common/enums';
import { Instance } from 'mobx-state-tree';
import {
  Button,
  ButtonGroup,
  Card,
  Tab,
  Tabs,
  TabId,
  Tooltip,
  Position,
  InputGroup,
  NumericInput,
} from '@blueprintjs/core';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import TranslatedMessage from '../common/translation/TranslatedMessage';
import MainSectionLayout from '../layout/MainSectionLayout';
import styled from '@emotion/styled';
import { css } from 'emotion';
import BaseGrid from '../common/grid/BaseGrid';
import BaseGridXL from '../common/grid/BaseGridXL';
import {
  ApplicationStore,
  ScoreStore,
  MatchStore,
  IndictmentStore,
  UiStore,
  PharmacyStore,
  PlannerStore,
} from '../../store';
import { renderGridDateTimeWithDetails } from '../../store/utils';
import { ICellRendererParams } from 'ag-grid-community';
import DateInput from '../common/input/DateInput';
import { IconNames } from '@blueprintjs/icons';
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Legend,
  Tooltip as ChartTooltip,
  Label,
  ReferenceLine,
} from 'recharts';
import Select from 'react-select';
import { getSemester, getFirstMonday, getFirstSunday } from '../../common/utils';

const getWithDiff = (newNumber, oldNumber) => {
  const diff = newNumber - oldNumber;
  if (diff === 0) return newNumber;
  else return newNumber + ' (' + (diff > 0 ? '+' : '') + diff + ')';
};

const dateCellRenderer = (params: ICellRendererParams) => {
  return params.getValue() ? params.getValue().split('T')[0] : '';
};

interface AnalysisPageProps {
  PharmacyStore?: Instance<typeof PharmacyStore>;
  PlannerStore?: Instance<typeof PlannerStore>;
  ApplicationStore?: Instance<typeof ApplicationStore>;
  ScoreStore?: Instance<typeof ScoreStore>;
  MatchStore?: Instance<typeof MatchStore>;
  IndictmentStore?: Instance<typeof IndictmentStore>;
  UiStore?: Instance<typeof UiStore>;
}

interface AnalysisPageState {
  isLoading: boolean;
  isMounted: boolean;
  planningId: number;
  selectedPlanningId: any;
  selectedTabId: string;
  selectedSubTabId: string;
  selectedDutyCountSubTabId: string;
  selectedUnbalancedCountSubTabId: string;
  startDate: Date;
  endDate: Date;
  prevStartDate: Date;
  prevEndDate: Date;
  ruleFilter: string;
  selectedDate?: Date;
  offset: number;
  savedPlanningId: number;
  savedPrevPlanningId: number;
  status: any;
  distribution: any;
  selectedStartRuleTransgressionDate: Date;
  selectedEndRuleTransgressionDate: Date;
  lowerGapLimit: number;
  upperGapLimit: number;
  semesterYear: number;
}

function getNextStartDate() {
  // JAN0 FEB1 MAR2 APR3 MAY4 JUN5 JUL6 AUG7 SEP8 OCT9 NOV10 DEC11
  const now = new Date();
  if (now.getMonth() < 6) {
    now.setMonth(6);
    now.setDate(1);
  } else {
    now.setFullYear(now.getFullYear() + 1);
    now.setMonth(0);
    now.setDate(1);
  }
  return now;
}

function getNextEndDate() {
  // JAN0 FEB1 MAR2 APR3 MAY4 JUN5 JUL6 AUG7 SEP8 OCT9 NOV10 DEC11
  const now = new Date();
  if (now.getMonth() < 6) {
    now.setFullYear(now.getFullYear() + 1);
    now.setMonth(0);
    now.setDate(1);
  } else {
    now.setFullYear(now.getFullYear() + 1);
    now.setMonth(6);
    now.setDate(1);
  }
  return now;
}

function getSemesterStartDate(date) {
  // JAN0 FEB1 MAR2 APR3 MAY4 JUN5 JUL6 AUG7 SEP8 OCT9 NOV10 DEC11
  const now = new Date(date);
  if (now.getMonth() >= 6) {
    now.setMonth(6);
    now.setDate(1);
  } else {
    // now.setFullYear(now.getFullYear() + 1);
    now.setMonth(0);
    now.setDate(1);
  }

  return now;
}

function getSemesterEndDate(date) {
  // JAN0 FEB1 MAR2 APR3 MAY4 JUN5 JUL6 AUG7 SEP8 OCT9 NOV10 DEC11
  const now = new Date(date);
  if (now.getMonth() >= 6) {
    // now.setFullYear(now.getFullYear() + 1);
    now.setMonth(11);
    now.setDate(31);
  } else {
    //    now.setFullYear(now.getFullYear() + 1);
    now.setMonth(5);
    now.setDate(30);
  }

  return now;
}

function balanceComparator(balanceA, balanceB) {
  const intBalanceA = parseInt(balanceA.split('('));
  const intBalanceB = parseInt(balanceB.split('('));
  return intBalanceA > intBalanceB ? 1 : intBalanceA < intBalanceB ? -1 : 0;
}

enum TAB {
  PROBLEMS = 'P',
  SCORES = 'S',
  MATCHES = 'M',
  TRANSGRESSIONS = 'T',
  RULES = 'R',
}
enum SUBTAB {
  TOOCLOSEDUTIES = 'D',
  ABNORMALDUTYCOUNTS = 'C',
  DUTYCOUNTCHANGES = 'DC',
  UNBALANCEDNEIGHBORS = 'N',
  SPECIALISSUES = 'S',
  GROUPANALYSIS = 'GA',
  INSUFFICIENTGEOCOVERAGE = 'G',
}

enum SUBSUBTAB {
  TOOCLOSEDUTIESGRID = 'DGRI',
  TOOCLOSEDUTIESGRAPHS = 'DGRA',
  DUTYCOUNTCHANGESGRID = 'DCGRI',
  DUTYCOUNTCHANGESGRAPHS = 'DCGRA',
  UNBALANCEDNEIGHBORSGRID = 'UNGRI',
  UNBALANCEDNEIGHBORSGRAPHS = 'UNGRA',
}

@inject('PharmacyStore', 'PlannerStore', 'ApplicationStore', 'UiStore')
@inject('ScoreStore', 'MatchStore', 'IndictmentStore')
@observer
class AnalysisPage extends React.Component<AnalysisPageProps & WithTranslateProps, AnalysisPageState> {
  startDate: Date;
  endDate: Date;
  selectedStartRuleTransgressionDate: Date;
  selectedEndRuleTransgressionDate: Date;
  prevStartDate: Date;
  prevEndDate: Date;
  constructor(props: any) {
    super(props);
    if (this.props.UiStore!.diagnosticPageStartDate == null) {
      this.startDate = getSemesterStartDate(new Date()); // getNextStartDate();
      this.endDate = getSemesterEndDate(new Date());
      this.setStartDate(this.startDate); // getNextEndDate();
      this.setEndDate(this.endDate); // getNextEndDate();
    } else {
      this.startDate = getSemesterStartDate(this.props.UiStore!.diagnosticPageStartDate); // getNextStartDate();
      this.endDate = getSemesterEndDate(this.props.UiStore!.diagnosticPageEndDate);
      this.selectedStartRuleTransgressionDate = this.startDate; // getNextEndDate();
      this.selectedEndRuleTransgressionDate = this.endDate; // getNextEndDate();
    }
    this.state = {
      isLoading: false,
      isMounted: false,
      planningId: -1,
      selectedPlanningId: {
        label: this.props.UiStore!.diagnosticPageCurrentPlanningId,
        value: this.props.UiStore!.diagnosticPageCurrentPlanningId,
      },
      selectedTabId: TAB.PROBLEMS,
      selectedSubTabId: SUBTAB.TOOCLOSEDUTIES,
      selectedDutyCountSubTabId: SUBSUBTAB.TOOCLOSEDUTIESGRAPHS,
      selectedUnbalancedCountSubTabId: SUBSUBTAB.UNBALANCEDNEIGHBORSGRAPHS,
      startDate: this.startDate,
      endDate: this.endDate,
      selectedStartRuleTransgressionDate: this.selectedStartRuleTransgressionDate,
      selectedEndRuleTransgressionDate: this.selectedEndRuleTransgressionDate,
      prevStartDate: this.prevStartDate,
      prevEndDate: this.prevEndDate,
      ruleFilter: '',
      selectedDate: undefined,
      offset: this.props.UiStore!.diagnosticOffset,
      savedPlanningId: this.props.UiStore!.diagnosticPageCurrentPlanningId,
      savedPrevPlanningId: this.props.UiStore!.diagnosticPagePrevPlanningId,
      status: this.props.UiStore!.diagnosticSearchStatus,
      distribution: new Map(),
      lowerGapLimit: 8,
      upperGapLimit: 50,
      semesterYear: new Date().getFullYear(),
    };
  }

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    this._isMounted && this.setState({ isMounted: true });

    const { UiStore } = this.props;

    this._isMounted &&
      this.setState({
        selectedTabId: UiStore!.diagnosticPageActiveTab == '' ? TAB.PROBLEMS : UiStore!.diagnosticPageActiveTab,
        selectedSubTabId:
          UiStore!.diagnosticPageActiveSubTab == '' ? SUBTAB.TOOCLOSEDUTIES : UiStore!.diagnosticPageActiveSubTab,
        selectedDutyCountSubTabId: SUBSUBTAB.DUTYCOUNTCHANGESGRAPHS,
        selectedUnbalancedCountSubTabId: SUBSUBTAB.UNBALANCEDNEIGHBORSGRAPHS,
        semesterYear: UiStore!.diagnosticPageCurrentSemesterYear,
      });

    // Tabs inside another Tabs messes up with the container height, resulting in a confusing scrollbar. Fix it for this page (try/catch because parentElement might be inaccessible)
    try {
      if (this.state.selectedTabId == TAB.PROBLEMS) {
        document.getElementsByClassName('bp3-tabs')[0].parentElement!.parentElement!.parentElement!.style!.overflow =
          'hidden';
      }
    } catch (error) {}
  }

  async componentWillUnmount() {
    this._isMounted && this.setState({ isMounted: false });
    this._isMounted = false;
  }

  async componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.notReadyYet()) return;
    const { ScoreStore, MatchStore, IndictmentStore, PlannerStore } = this.props;
    if (
      this.state.selectedTabId === TAB.SCORES ||
      this.state.selectedTabId === TAB.MATCHES ||
      this.state.selectedTabId === TAB.TRANSGRESSIONS
    ) {
      if (this.state.planningId === -1 || this.state.planningId !== prevState.planningId) {
        if (!this.state.isMounted) return;
        this._isMounted && this.setState({ isLoading: true });
        await ScoreStore!.getScores({});
        if (!this.state.isMounted) return;
        let planningId = this.state.planningId;
        if (planningId === -1) {
          planningId = Math.max(
            ...ScoreStore!.dataList.map((o) => {
              return o.scoreId;
            }),
          );
        }
        if (planningId > 0) {
          await Promise.all([
            MatchStore!.getMatches({ PlanningId: planningId }),
            MatchStore!.getComparedMatches({ PlanningId: planningId }),
            IndictmentStore!.getIndictments({ PlanningId: planningId }),
            // IndictmentStore!.getIndictmentsCount(planningId),
            MatchStore!.GetGroupTransgressions(this.state.planningId, '*', {}),
            MatchStore!.GetGroupTransgressionsByDate(
              this.state.startDate,
              this.state.endDate,
              this.state.planningId,
              '*',
              {},
            ),
          ]);
          if (!this.state.isMounted) return;
          this._isMounted && this.setState({ planningId });
        }

        this._isMounted && this.setState({ isLoading: false });
      }
    } else if (this.state.planningId !== -1 && this.state.selectedTabId == TAB.RULES) {
      await PlannerStore!.getRules({ PlanningId: this.state.planningId });
    }
  }

  GetTooCloseDutiesColumns = [
    {
      headerName: this.props.translate(TLCode.TOO_CLOSE_DUTIES_PHARMACY),
      maxWidth: 80,
      minWidth: 70,
      field: 'apbNumber',
      sortingOrder: ['asc', 'desc'],
      resizable: false,
    },
    {
      headerName: this.props.translate(TLCode.TOO_CLOSE_DUTIES_PHARMACY_GROUP),
      maxWidth: 90,
      minWidth: 60,
      field: 'group',
      sortingOrder: ['asc', 'desc'],
      resizable: false,
    },
    {
      headerName: this.props.translate(TLCode.TOO_CLOSE_DUTIES_PHARMACY_NAME),
      maxWidth: 600,
      width: 600,
      minWidth: 250,
      field: 'pharmacyName',
      sortingOrder: ['asc', 'desc'],
    },
    {
      headerName: this.props.translate(TLCode.TOO_CLOSE_DUTIES_DATE1),
      maxWidth: 160,
      minWidth: 90,
      field: 'date1',
      cellRenderer: dateCellRenderer,
      sortingOrder: ['asc', 'desc'],
    },
    {
      headerName: this.props.translate(TLCode.TOO_CLOSE_DUTIES_DATE2),
      maxWidth: 160,
      minWidth: 70,
      // width: 30,
      field: 'date2',
      cellRenderer: dateCellRenderer,
      sortingOrder: ['asc', 'desc'],
    },
    {
      headerName: this.props.translate(TLCode.TOO_CLOSE_DUTIES_ECART),
      // maxWidth: 160,
      minWidth: 50,
      // width: 30,
      field: 'ecart',
      sortingOrder: ['asc', 'desc'],
    },
    {
      headerName: this.props.translate(TLCode.TOO_CLOSE_DUTIES_TOTAL),
      // maxWidth: 160,
      minWidth: 50,
      // width: 30,
      field: 'total',
      sortingOrder: ['asc', 'desc'],
    },
  ];

  GetAbnormalDutyCountsColumns = [
    {
      headerName: this.props.translate(TLCode.TOO_CLOSE_DUTIES_PHARMACY),
      width: 25,
      field: 'apbNumber',
      sortingOrder: ['asc', 'desc'],
      resizable: false,
    },
    {
      headerName: this.props.translate(TLCode.TOO_CLOSE_DUTIES_PHARMACY_GROUP),
      width: 25,
      field: 'group',
      sortingOrder: ['asc', 'desc'],
      resizable: false,
    },
    {
      headerName: this.props.translate(TLCode.TOO_CLOSE_DUTIES_PHARMACY_NAME),
      field: 'pharmacyName',
      sortingOrder: ['asc', 'desc'],
    },
    {
      headerName: this.props.translate(TLCode.ABNORMAL_DUTY_COUNTS_TARGET_COUNT),
      width: 35,
      field: 'targetDutyCount',
    },
    {
      headerName: this.props.translate(TLCode.ABNORMAL_DUTY_COUNTS_COUNT),
      width: 35,
      field: 'dayDutyCount',
      sortingOrder: ['asc', 'desc'],
    },
    {
      headerName: 'delta',
      width: 35,
      valueGetter:
        'Number.isNaN(getValue("dayDutyCount")-getValue("targetDayDutyCount")) ? 0:getValue("dayDutyCount")-getValue("targetDutyCount")',
      sortingOrder: ['asc', 'desc'],
    },
    {
      headerName: this.props.translate(TLCode.ABNORMAL_DUTY_COUNTS_DAY_TARGET_COUNT),
      width: 30,
      field: 'targetDayDutyCount',
    },
    {
      headerName: this.props.translate(TLCode.ABNORMAL_DUTY_COUNTS_DAY_COUNT),
      width: 30,
      valueGetter:
        'Number.isNaN(getValue("dayDutyCount")-getValue("nightDutyCount")) ? 0:getValue("dayDutyCount")-getValue("nightDutyCount")',
      sortingOrder: ['asc', 'desc'],
    },
    {
      headerName: this.props.translate(TLCode.ABNORMAL_DUTY_COUNTS_NIGHT_TARGET_COUNT),
      width: 30,
      field: 'targetNightDutyCount',
    },
    {
      headerName: this.props.translate(TLCode.ABNORMAL_DUTY_COUNTS_NIGHT_COUNT),
      width: 30,
      field: 'nightDutyCount',
      sortingOrder: ['asc', 'desc'],
    },
  ];

  GetDutyCountChangesColumns = [
    {
      headerName: this.props.translate(TLCode.DUTY_COUNT_CHANGES_APBNUMBER),
      width: 30,
      field: 'apbNumber',
      sortingOrder: ['asc', 'desc'],
      resizable: false,
    },
    {
      headerName: this.props.translate(TLCode.TOO_CLOSE_DUTIES_PHARMACY_GROUP),
      width: 30,
      field: 'group',
      resizable: false,
    },
    {
      headerName: this.props.translate(TLCode.DUTY_COUNT_CHANGES_PHARMACY_NAME),
      // width: 40,
      field: 'pharmacyName',
      sortingOrder: ['asc', 'desc'],
      resizable: true,
    },

    {
      headerName: this.props.translate(TLCode.DUTY_COUNT_CHANGES_GROUP_TARGET),
      width: 30,
      field: 'target',
      sortingOrder: ['asc', 'desc'],
      resizable: false,
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.DUTY_COUNT_CHANGES_CURRDAY),
      width: 30,
      field: 'currDay',
      sortingOrder: ['asc', 'desc'],
      cellStyle: { 'background-color': '#f9f998' },
      headerTooltip: this.props.translate(TLCode.DUTY_COUNT_CHANGES_CURRNBR_TOOLTIP),
      filter: false,
      resizable: true,
    },
    {
      headerName: this.props.translate(TLCode.DUTY_COUNT_CHANGES_GROUP_TARGET_DIFFERENCE),
      width: 30,
      valueGetter:
        'Number.isNaN(getValue("currDay") - getValue("target")) ? 0:getValue("currDay") - getValue("target")',
      sortingOrder: ['asc', 'desc'],
      cellStyle: (params) => {
        if (params.value > 2) {
          // mark police cells as red
          return { backgroundColor: '#ffb6c1' };
        } else if (params.value < -2) {
          // mark police cells as red
          return { backgroundColor: '#ffb6c1' };
          // } else if (Math.abs(params.value) <= 1) {
          //   return {backgroundColor: '#90EE90'};
        } else return null;
      },
      headerTooltip: this.props.translate(TLCode.DUTY_COUNT_CHANGES_DIFFERENCE_TOOLTIP),
      filter: false,
    },
    // {
    //   headerName: this.props.translate(TLCode.DUTY_COUNT_CHANGES_PREVDAY),
    //   width: 30,
    //   field: 'prevDay',
    //   sortingOrder: ['asc', 'desc'],
    //   headerTooltip: this.props.translate(TLCode.DUTY_COUNT_CHANGES_PREVNBR_TOOLTIP),
    //   filter: false,
    // },
    // {
    //   headerName: this.props.translate(TLCode.DUTY_COUNT_CHANGES_DIFFERENCE),
    //   width: 30,
    //   valueGetter: 'Number.isNaN(getValue("currDay")-getValue("prevDay")) ? 0:getValue("currDay")-getValue("prevDay")',
    //   sortingOrder: ['asc', 'desc'],
    //   cellStyle: (params) => {
    //     if (params.value > 2) {
    //       // mark police cells as red
    //       return { backgroundColor: '#ffb6c1' };
    //     } else if (params.value < -2) {
    //       // mark police cells as red
    //       return { backgroundColor: '#ffb6c1' };
    //       // } else if (Math.abs(params.value) <= 1) {
    //       //   return {backgroundColor: '#90EE90'};
    //     } else return null;
    //   },
    //   headerTooltip: this.props.translate(TLCode.DUTY_COUNT_CHANGES_DIFFERENCE_TOOLTIP),
    //   filter: false,
    // },
    {
      headerName: this.props.translate(TLCode.DUTY_COUNT_CHANGES_GROUP_DAY_TARGET),
      width: 30,
      field: 'dayTarget',
      sortingOrder: ['asc', 'desc'],
      resizable: false,
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.DUTY_COUNT_CHANGES_CURRDAYONLY),
      width: 30,
      valueGetter:
        'Number.isNaN(getValue("currDay")-getValue("currNight"))?null:getValue("currDay")-getValue("currNight")',
      sortingOrder: ['asc', 'desc'],
      cellStyle: { 'background-color': '#f9f998' },
      headerTooltip: this.props.translate(TLCode.DUTY_COUNT_CHANGES_CURRDAYONLY_TOOLTIP),
      filter: false,
    },
    // {
    //   headerName: this.props.translate(TLCode.DUTY_COUNT_CHANGES_PREVDAYONLY),
    //   width: 30,
    //   valueGetter:
    //     'Number.isNaN(getValue("prevDay")-getValue("prevNight"))?null:getValue("prevDay")-getValue("prevNight")',
    //   sortingOrder: ['asc', 'desc'],
    //   headerTooltip: this.props.translate(TLCode.DUTY_COUNT_CHANGES_PREVDAYONLY_TOOLTIP),
    //   filter: false,
    // },
    {
      headerName: this.props.translate(TLCode.DUTY_COUNT_CHANGES_GROUP_NIGHT_TARGET),
      width: 30,
      field: 'nightTarget',
      sortingOrder: ['asc', 'desc'],
      resizable: false,
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.DUTY_COUNT_CHANGES_CURRNIGHT),
      width: 30,
      field: 'currNight',
      sortingOrder: ['asc', 'desc'],
      cellStyle: { 'background-color': '#f9f998' },
      // headerComponentFramework: HeaderTooltip,
      // headerComponentParams: {
      //   title: this.props.translate(TLCode.DUTY_COUNT_CHANGES_CURRNIGHT),
      //   tooltip: this.props.translate(TLCode.DUTY_COUNT_CHANGES_CURRNIGHT_TOOLTIP),
      // },
      headerTooltip: this.props.translate(TLCode.DUTY_COUNT_CHANGES_CURRNIGHT_TOOLTIP),
      filter: false,
      resizable: true,
    },
    // {
    //   headerName: this.props.translate(TLCode.DUTY_COUNT_CHANGES_PREVNIGHT),
    //   width: 30,
    //   field: 'prevNight',
    //   sortingOrder: ['asc', 'desc'],
    //   headerTooltip: this.props.translate(TLCode.DUTY_COUNT_CHANGES_PREVNIGHT_TOOLTIP),
    //   filter: false,
    // },
    {
      headerName: this.props.translate(TLCode.DUTY_COUNT_CHANGES_CURRENT_RATIO),
      width: 30,
      // field: 'currentRatio',
      valueGetter:
        '(Number.isNaN(getValue("currDay")) || getValue("currDay") == 0) ? "n/a":((getValue("currDay") - getValue("currNight"))/getValue("currDay")).toFixed(2)',
      sortingOrder: ['asc', 'desc'],
      cellStyle: { 'background-color': '#f9f998' },
      headerTooltip: this.props.translate(TLCode.DUTY_COUNT_CHANGES_CURRRATIO_TOOLTIP),
      filter: false,
    },
    // {
    //   headerName: this.props.translate(TLCode.DUTY_COUNT_CHANGES_PREVIOUS_RATIO),
    //   width: 30,
    //   // field: 'previousRatio',
    //   valueGetter:
    //     '(Number.isNaN(getValue("prevDay")) || getValue("prevDay") == 0) ? "n/a":((getValue("prevDay") - getValue("prevNight"))/getValue("prevDay")).toFixed(2)',
    //   sortingOrder: ['asc', 'desc'],
    //   headerTooltip: this.props.translate(TLCode.DUTY_COUNT_CHANGES_PREVRATIO_TOOLTIP),
    //   filter: false,
    // },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_FRIDAYS),
      width: 25,
      field: 'currFridays',
      sortingOrder: ['asc', 'desc'],
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_FRIDAYS_TOOLTIP),
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_SATURDAYS),
      width: 25,
      field: 'currSaturdays',
      sortingOrder: ['asc', 'desc'],
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_SATURDAYS_TOOLTIP),
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_SUNDAYS),
      width: 25,
      field: 'currSundays',
      sortingOrder: ['asc', 'desc'],
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_SUNDAYS_TOOLTIP),
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_HOLIDAYS),
      width: 25,
      field: 'currBankHolidays',
      sortingOrder: ['asc', 'desc'],
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_HOLIDAYS_TOOLTIP),
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_YEAR_HOLIDAYS),
      width: 25,
      field: 'yearBankHolidays',
      sortingOrder: ['asc', 'desc'],
      headerTooltip: 'Year', // this.props.translate(TLCode.UNBALANCED_NEIGHBORS_HOLIDAYS_TOOLTIP),
      filter: false,
    },
  ];

  GetBlackpointTransgressionsColumns = [
    {
      headerName: this.props.translate(TLCode.DUTY_COUNT_CHANGES_APBNUMBER),
      width: 40,
      field: 'apbNumber',
      sort: 'asc',
      sortedAt: 0,
      sortingOrder: ['asc', 'desc'],
      resizable: false,
    },
    {
      headerName: this.props.translate(TLCode.TOO_CLOSE_DUTIES_PHARMACY_GROUP),
      width: 30,
      field: 'group',
      resizable: false,
    },
    {
      headerName: this.props.translate(TLCode.SPECIALISSUES_NAME),
      field: 'pharmacyName',
      sortingOrder: ['asc', 'desc'],
      resizable: true,
    },
    {
      headerName: this.props.translate(TLCode.SPECIALISSUES_DATE),
      width: 60,
      sort: 'asc',
      sortedAt: 1,
      field: 'date',
      cellRenderer: dateCellRenderer,
      resizable: false,
    },
    {
      headerName: this.props.translate(TLCode.SPECIALISSUES_TYPE),
      width: 40,
      field: 'type',
      resizable: false,
    },
  ];

  GetGroupTransgressionsColumns = [
    {
      headerName: this.props.translate(TLCode.SPECIALISSUES_DATE),
      width: 125,
      field: 'date',
      cellRenderer: dateCellRenderer,
      sort: 'asc',
      sortedAt: 0,
      resizable: false,
    },
    {
      headerName: this.props.translate(TLCode.TOO_CLOSE_DUTIES_PHARMACY_GROUP),
      width: 300,
      field: 'group',
      sort: 'asc',
      sortedAt: 1,
      resizable: false,
    },
  ];

  GetGroupTransgressionsByDateColumns = [
    {
      headerName: this.props.translate(TLCode.SPECIALISSUES_DATE),
      width: 125,
      field: 'date',
      cellRenderer: dateCellRenderer,
      sort: 'asc',
      sortedAt: 0,
      resizable: false,
    },
    {
      headerName: this.props.translate(TLCode.TOO_CLOSE_DUTIES_PHARMACY_GROUP),
      width: 100,
      field: 'group',
      sort: 'asc',
      sortedAt: 1,
      resizable: false,
    },
  ];

  GroupRuleTransgressionColumns = [
    {
      headerName: this.props.translate(TLCode.SPECIALISSUES_DATE),
      width: 400,
      field: 'msg',
      sort: 'asc',
      sortedAt: 0,
      resizable: false,
    },
  ];

  GroupAverageDutyCountColumns = [
    {
      headerName: this.props.translate(TLCode.GROUPANALYSIS_GROUP),
      width: 70,
      field: 'group',
      sort: 'asc',
      sortedAt: 0,
      resizable: false,
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.GROUPANALYSIS_TARGET),
      width: 60,
      field: 'targetCount',
      resizable: false,
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.GROUPANALYSIS_AVERAGE),
      width: 70,
      field: 'average',
      valueFormatter: (params) => {
        try {
          return params.data.average.toFixed(2);
        } catch (error) {
          return 0;
        }
      },
      resizable: false,
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.GROUPANALYSIS_MEMBERS),
      width: 70,
      field: 'members',
      resizable: false,
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.GROUPANALYSIS_COUNT),
      width: 60,
      field: 'count',
      resizable: false,
      filter: false,
    },
  ];

  GetUnbalancedNeighborsColumns = [
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_PHARMACY_APB),
      width: 30,
      field: 'apbNumber',
      sortingOrder: ['asc', 'desc'],
    },
    {
      headerName: this.props.translate(TLCode.TOO_CLOSE_DUTIES_PHARMACY_GROUP),
      width: 30,
      field: 'group',
      sortingOrder: ['asc', 'desc'],
      resizable: false,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_PHARMACY_NAME),
      field: 'pharmacyName',
      sortingOrder: ['asc', 'desc'],
      resizable: true,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_FRIDAYS),
      width: 25,
      field: 'fridayCnt',
      sortingOrder: ['asc', 'desc'],
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_FRIDAYS_TOOLTIP),
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_FRIDAY_STATS),
      width: 25,
      field: 'fridays',
      sortingOrder: ['asc', 'desc'],
      cellStyle: (params) => params.data.fridayOk,
      comparator: balanceComparator,
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_FRIDAYS_STAT_TOOLTIP),
      filter: false,
      hide: true,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_SATURDAYS),
      width: 25,
      field: 'saturdayCnt',
      sortingOrder: ['asc', 'desc'],
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_SATURDAYS_TOOLTIP),
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_SATURDAY_STATS),
      width: 25,
      field: 'saturdays',
      sortingOrder: ['asc', 'desc'],
      cellStyle: (params) => params.data.saturdayOk,
      comparator: balanceComparator,
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_SATURDAYS_TOOLTIP),
      filter: false,
      hide: true,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_SUNDAYS),
      width: 25,
      field: 'sundayCnt',
      sortingOrder: ['asc', 'desc'],
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_SUNDAYS_TOOLTIP),
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_SUNDAY_STATS),
      width: 25,
      field: 'sundays',
      sortingOrder: ['asc', 'desc'],
      cellStyle: (params) => params.data.sundayOk,
      comparator: balanceComparator,
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_SUNDAYS_TOOLTIP),
      filter: false,
      hide: true,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_HOLIDAYS),
      width: 25,
      field: 'holidayCnt',
      sortingOrder: ['asc', 'desc'],
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_HOLIDAYS_TOOLTIP),
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_YEAR_HOLIDAYS),
      width: 25,
      field: 'yearHolidayCnt',
      sortingOrder: ['asc', 'desc'],
      headerTooltip: 'Year', // this.props.translate(TLCode.UNBALANCED_NEIGHBORS_HOLIDAYS_TOOLTIP),
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_HOLIDAY_STATS),
      width: 25,
      field: 'holidays',
      sortingOrder: ['asc', 'desc'],
      cellStyle: (params) => params.data.holidayOk,
      comparator: balanceComparator,
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_HOLIDAYS_TOOLTIP),
      filter: false,
      hide: true,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_NIGHTS),
      width: 25,
      field: 'nightCnt',
      sortingOrder: ['asc', 'desc'],
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_NIGHTS_TOOLTIP),
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_NIGHT_STATS),
      width: 25,
      field: 'nights',
      sortingOrder: ['asc', 'desc'],
      cellStyle: (params) => params.data.nightOk,
      comparator: balanceComparator,
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_NIGHTS_TOOLTIP),
      filter: false,
      hide: true,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_DAYS),
      width: 25,
      valueGetter:
        'Number.isNaN(getValue("totalCnt")-getValue("nightCnt"))?null:getValue("totalCnt")-getValue("nightCnt")',
      sortingOrder: ['asc', 'desc'],
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_DAYS_TOOLTIP),
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_TOTAL),
      width: 25,
      field: 'totalCnt',
      sortingOrder: ['asc', 'desc'],
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_TOTAL_TOOLTIP),
      filter: false,
    },
    {
      headerName: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_TOTAL_STATS),
      width: 25,
      field: 'total',
      sortingOrder: ['asc', 'desc'],
      cellStyle: (params) => params.data.totalOk,
      comparator: balanceComparator,
      headerTooltip: this.props.translate(TLCode.UNBALANCED_NEIGHBORS_TOTAL_STAT_TOOLTIP),
      filter: false,
      hide: true,
    },
  ];

  redGreen(delta) {
    if (delta <= 2) {
      return { color: '#008517', backgroundColor: '#d2fad9' };
    } else {
      return { color: '#ff2000', backgroundColor: '#f7dbd7' };
    }
  }

  scoreColumnDefs = [
    {
      headerName: this.props.translate(TLCode.ANALYSIS_SCORES_TIMESTAMP),
      width: 100,
      field: 'createdOn',
      cellRenderer: renderGridDateTimeWithDetails,
    },
    { headerName: this.props.translate(TLCode.ANALYSIS_SCORES_PLANNINGID), width: 50, field: 'planningId' },
    { headerName: this.props.translate(TLCode.ANALYSIS_SCORES_RUNID), width: 50, field: 'runId' },
    // { headerName: this.props.translate(TLCode.ANALYSIS_SCORES_ID), width: 50, field: 'planningId', sort: 'desc' },
    { headerName: this.props.translate(TLCode.ANALYSIS_SCORES_ID), width: 50, field: 'scoreId', sort: 'desc' },
    { headerName: this.props.translate(TLCode.ANALYSIS_SCORES_STATUS), width: 65, field: 'status' },
    {
      headerName: this.props.translate(TLCode.ANALYSIS_SCORES_START),
      width: 60,
      field: 'startDate',
      cellRenderer: dateCellRenderer,
    },
    {
      headerName: this.props.translate(TLCode.ANALYSIS_SCORES_END),
      width: 60,
      field: 'endDate',
      cellRenderer: dateCellRenderer,
    },
    { headerName: this.props.translate(TLCode.ANALYSIS_SCORES_NAME), width: 100, field: 'name' },
    { headerName: this.props.translate(TLCode.ANALYSIS_SCORES_DESCRIPTION), field: 'description' },
    { headerName: this.props.translate(TLCode.ANALYSIS_SCORES_HARD), width: 65, field: 'hardScore' },
    { headerName: this.props.translate(TLCode.ANALYSIS_SCORES_MED), width: 65, field: 'mediumScore' },
    { headerName: this.props.translate(TLCode.ANALYSIS_SCORES_SOFT), width: 65, field: 'softScore' },
  ];

  solutionDetailColumns = [
    { headerName: this.props.translate(TLCode.ANALYSIS_MATCHES_RULE), width: 200, field: 'ruleName' },
    {
      headerName: this.props.translate(TLCode.ANALYSIS_MATCHES_TRANSGRESSIONS),
      width: 200,
      field: 'transgressionCount',
      cellRenderer: (params: ICellRendererParams) => {
        return getWithDiff(params.data.transgressionCount, params.data.previousTransgressionCount);
      },
    },
    {
      headerName: this.props.translate(TLCode.ANALYSIS_MATCHES_HARD),
      width: 200,
      field: 'hardScore',
      cellRenderer: (params: ICellRendererParams) => {
        return getWithDiff(params.data.hardScore, params.data.previousHardScore);
      },
    },
    {
      headerName: this.props.translate(TLCode.ANALYSIS_MATCHES_MED),
      width: 200,
      field: 'mediumScore',
      cellRenderer: (params: ICellRendererParams) => {
        return getWithDiff(params.data.mediumScore, params.data.previousMediumScore);
      },
    },
    {
      headerName: this.props.translate(TLCode.ANALYSIS_MATCHES_SOFT),
      width: 200,
      field: 'softScore',
      cellRenderer: (params: ICellRendererParams) => {
        return getWithDiff(params.data.softScore, params.data.previousSoftScore);
      },
    },
  ];

  indictmentColumnDefs = [
    { headerName: this.props.translate(TLCode.ANALYSIS_MATCHES_RULE), width: 200, field: 'description' },
    {
      headerName: this.props.translate(TLCode.ANALYSIS_MATCHES_SOFT),
      width: 60,
      field: 'apbnr',
      cellRenderer: (params: ICellRendererParams) => {
        return params.data.description.split('$')[1];
      },
    },
  ];

  indictmentCountColumnDefs = [
    { headerName: this.props.translate(TLCode.ANALYSIS_MATCHES_APPNUMBER), field: 'apbNumber' },
    { headerName: this.props.translate(TLCode.ANALYSIS_MATCHES_NAME), field: 'name' },
    {
      headerName: this.props.translate(TLCode.ANALYSIS_MATCHES_COUNT),
      field: 'cnt',
    },
  ];

  setFilterText(ruleFilter) {
    this.setState({
      ruleFilter,
    });
  }

  goPrevious = () => {
    if (this.state.isMounted) this.setState({ planningId: this.state.planningId - 1 });
  };

  goNext = () => {
    if (this.state.isMounted) this.setState({ planningId: this.state.planningId + 1 });
  };

  handleTabChange = (newTabId: TabId, prevTabId: TabId, event: any) => {
    if (this.state.isMounted) {
      this.setState({ selectedTabId: newTabId.toString() });
      this.props.UiStore!.setDiagnosticPageActiveTab(newTabId.toString());
      if (newTabId === TAB.SCORES && this.props.ScoreStore!.dataList.length === 0) this.props.ScoreStore!.getScores({});
    }
  };

  handleSubTabChange = (newTabId: TabId, prevTabId: TabId, event: any) => {
    if (this.state.isMounted) {
      this.setState({ selectedSubTabId: newTabId.toString() });
      this.props.UiStore!.setDiagnosticPageActiveSubTab(newTabId.toString());
      if (newTabId === SUBTAB.INSUFFICIENTGEOCOVERAGE && this.props.ScoreStore!.dataList.length === 0) {
        this.props.ScoreStore!.getScores({});
      }
    }
  };

  handleSubSubTabChange = (newTabId: TabId, prevTabId: TabId, event: any) => {
    if (this.state.isMounted) {
      if (typeof newTabId === 'string') {
        // this.setState({ selectedDutyCountSubTabId: newTabId.toString() });
        newTabId.startsWith('DC')
          ? this.setState({ selectedDutyCountSubTabId: newTabId.toString() })
          : newTabId.startsWith('UN')
          ? this.setState({ selectedUnbalancedCountSubTabId: newTabId.toString() })
          : true;
      } else {
        // this.setState({ selectedDutyCountSubTabId: newTabId.toString() });
      }
      //   // this.setState({ selectedDutyCountSubTabId: newTabId.toString() });
      this.props.UiStore!.setDiagnosticPageActiveSubTab(newTabId.toString());
    }
  };

  getSolutionLabel = (planning) => {
    if (planning === undefined) return <span />;
    else {
      return (
        <span>
          #{planning!.scoreId} [{planning!.createdOn}] (HARD:{planning!.hardScore} / MED:{planning!.mediumScore} / SOFT:
          {planning!.softScore})
        </span>
      );
    }
  };

  onScoreSelected = (event) => {
    const scoreId = event.data.scoreId;
    if (this.state.isMounted) {
      this.setState({ planningId: scoreId, savedPlanningId: scoreId, selectedTabId: TAB.MATCHES });
      this.props.UiStore!.setDiagnosticPageCurrentPlanningId(scoreId);
    }
  };

  onRuleSelected = (event) => {
    const ruleName = event.data.ruleName;
    if (this.state.isMounted) this.setState({ ruleFilter: ruleName, selectedTabId: TAB.TRANSGRESSIONS });
  };

  onPharmacySelected = (event) => {
    const pharmacyId = event.data.pharmacy;
    this.props.ApplicationStore!.navigate(`${Page.PharmacyPage}/${pharmacyId}`);
  };

  onPharmacyIdSelected = (event) => {
    const pharmacyId = event.data.pharmacyId;
    this.props.ApplicationStore!.navigate(`${Page.PharmacyPage}/${pharmacyId}`);
  };

  onApbNrSelected = async (event) => {
    const apbNr = event.data.description.split('$')[1];
    this.navigate(apbNr);
    console.log('apbNrSelected');
  };

  onApbNrSelected2 = async (event) => {
    const apbNr = event.data.apbNumber;
    this.setState({ ruleFilter: apbNr });
  };

  navigate = async (apbNr) => {
    await this.props.PharmacyStore!.loadList();
    const pharmacy = this.props.PharmacyStore!.dataList.filter((ph) => ph.apbNumber === parseInt(apbNr))[0];
    const pharmacyId = pharmacy.pharmacyId;
    this.props.ApplicationStore!.navigate(`${Page.PharmacyPage}/${pharmacyId}`);
  };

  setStartDate = (date) => {
    // if (this.state.isMounted) {
    this.setState({ startDate: date, selectedStartRuleTransgressionDate: date, selectedTabId: TAB.PROBLEMS });
    this.props.UiStore!.setDiagnosticPageStartDate(date);
    // }
  };

  setEndDate = (date) => {
    // if (this.state.isMounted) {
    this.setState({ endDate: date, selectedEndRuleTransgressionDate: date, selectedTabId: TAB.PROBLEMS });
    this.props.UiStore!.setDiagnosticPageEndDate(date);
    // }
  };

  getGlobalPerformancePercentage = () => {
    const { MatchStore } = this.props;
    if (this.notReadyYet()) {
      return 'n/a';
    } else {
      const tooCloseDuties = parseFloat(MatchStore!.miscDatas['GetTooCloseDutiesPercentage']);
      const abnormalCount = parseFloat(MatchStore!.miscDatas['GetAbnormalDutyCountsPercentage']);
      const unbalancedNeighbors = parseFloat(MatchStore!.miscDatas['GetUnbalancedNeighborsPercentage']);
      return (100 - (tooCloseDuties + abnormalCount + unbalancedNeighbors) / 3).toFixed(2);
    }
  };

  notReadyYet = () => {
    return this.state.isLoading || !this.state.isMounted;
  };

  formatNeighborsComparison = (pharmacyNbr, neighborsAverage) => {
    try {
      return `\u0078\u0305 ${neighborsAverage.toFixed(0)}, Δ ${(pharmacyNbr - neighborsAverage).toFixed(0)}`;
    } catch (error) {
      console.error(error);
    }
  };

  getUnbalancedNeighborsList = (list) => {
    return list.map((elem) => ({
      pharmacyId: elem.pharmacyId,
      apbNumber: elem.apbNumber,
      pharmacyName: elem.pharmacyName,
      group: elem.group,
      fridayCnt: elem.fridays,
      fridays: this.formatNeighborsComparison(elem.fridays, elem.neighborsFridays),
      fridayOk: this.redGreen(Math.abs(elem.fridays - elem.neighborsFridays)),
      saturdayCnt: elem.saturdays,
      saturdays: this.formatNeighborsComparison(elem.saturdays, elem.neighborsSaturdays),
      saturdayOk: this.redGreen(Math.abs(elem.saturdays - elem.neighborsSaturdays)),
      sundayCnt: elem.sundays,
      sundays: this.formatNeighborsComparison(elem.sundays, elem.neighborsSundays),
      sundayOk: this.redGreen(Math.abs(elem.sundays - elem.neighborsSundays)),
      holidayCnt: elem.holidays,
      yearHolidayCnt: elem.yearHolidays,
      holidays: this.formatNeighborsComparison(elem.holidays, elem.neighborsHolidays),
      holidayOk: this.redGreen(Math.abs(elem.holidays - elem.neighborsHolidays)),
      nightCnt: elem.nights,
      nights: this.formatNeighborsComparison(elem.nights, elem.neighborsNights),
      nightOk: this.redGreen(Math.abs(elem.nights - elem.neighborsNights)),
      totalCnt: elem.total,
      total: this.formatNeighborsComparison(elem.total, elem.neighborsTotal),
      totalOk: this.redGreen(Math.abs(elem.total - elem.neighborsTotal)),
    }));
  };

  getFinalDutyCountChangesList = (list) => {
    return list.map((elem) => ({
      ...elem,
      currentRatio: (elem.currNight / elem.currDay).toFixed(2),
      previousRatio: (elem.prevNight / elem.prevDay).toFixed(2),
    }));
  };

  getFinalBlackpointTransgressionList = (list) => {
    return list.map((elem) => ({
      ...elem,
      // currentRatio: (elem.currNight / elem.currDay).toFixed(2),
      // previousRatio: (elem.prevNight / elem.prevDay).toFixed(2),
    }));
  };

  getFinalGroupTransgressionList = (list) => {
    return list.map((elem) => ({
      ...elem,
      // currentRatio: (elem.currNight / elem.currDay).toFixed(2),
      // previousRatio: (elem.prevNight / elem.prevDay).toFixed(2),
    }));
  };

  getFinalGroupTransgressionByDateList = (list) => {
    return list.map((elem) => ({
      ...elem,
      // currentRatio: (elem.currNight / elem.currDay).toFixed(2),
      // previousRatio: (elem.prevNight / elem.prevDay).toFixed(2),
    }));
  };

  selectedDateChange = async (date) => {
    this.setState({ selectedDate: date });
    this.startDate = date;
    const date2 = new Date(JSON.parse(JSON.stringify(date)));
    date2.setMonth(date2.getMonth() - 6);

    const semester = getSemester(new Date(date2));
    this.setStartDate(new Date(semester.beginningStr));
    this.setEndDate(new Date(semester.endStr));
  };

  setS1 = () => {
    this.handlePlanningIdValueChange(-1, '-1');
    this.setState({ selectedPlanningId: {} });
    let date = getFirstMonday(this.state.semesterYear, 0);
    this.setStartDate(date);

    date = getFirstSunday(this.state.semesterYear, 6);
    this.setEndDate(date);
  };

  setS2 = () => {
    this.handlePlanningIdValueChange(-1, '-1');
    this.setState({ selectedPlanningId: {} });
    let date = getFirstMonday(this.state.semesterYear, 6);
    this.setStartDate(date);

    date = getFirstSunday(this.state.semesterYear + 1, 0);
    this.setEndDate(date);
  };

  selectedStartRuleTransgressionChange = async (date) => {
    this.setState({ selectedStartRuleTransgressionDate: date });
    this.selectedStartRuleTransgressionDate = date;
  };

  selectedEndRuleTransgressionChange = async (date) => {
    this.setState({ selectedEndRuleTransgressionDate: date });
    this.selectedEndRuleTransgressionDate = date;
  };

  onFirstDateBlur = () => {
    // const semester = getSemester(new Date(this.state.firstDate));
    // this.setState({ lastDate: semester.endStr, startDate: semester.beginningStr, firstDate: semester.beginningStr, runName: 'RUN-'+semester.name })
  };

  calculateCurrDutyTotal = (changes) => {
    let total = 0;
    changes
      ? changes.map((ph) => {
          total += Number.isNaN(ph.currDay) ? 0 : ph.currDay;
        })
      : 0;
    return total;
  };

  calculatePrevDutyTotal = (changes) => {
    let total = 0;
    changes
      ? changes.map((ph) => {
          total += Number.isNaN(ph.prevDay) ? 0 : ph.prevDay;
        })
      : 0;
    return total;
  };

  calculateCurrDayDutyTotal = (changes) => {
    let total = 0;
    changes
      ? changes.map((ph) => {
          total += Number.isNaN(ph.currDay - ph.currNight) ? 0 : ph.currDay - ph.currNight;
        })
      : 0;
    return total;
  };

  calculatePrevDayDutyTotal = (changes) => {
    let total = 0;
    changes
      ? changes.map((ph) => {
          total += Number.isNaN(ph.prevDay - ph.prevNight) ? 0 : ph.prevDay - ph.prevNight;
        })
      : 0;
    return total;
  };

  calculateCurrNightDutyTotal = (changes) => {
    let total = 0;
    changes
      ? changes.map((ph) => {
          total += Number.isNaN(ph.currNight) ? 0 : ph.currNight;
        })
      : 0;
    return total;
  };

  calculatePrevNightDutyTotal = (changes) => {
    let total = 0;
    changes
      ? changes.map((ph) => {
          total += Number.isNaN(ph.prevNight) ? 0 : ph.prevNight;
        })
      : 0;
    return total;
  };

  calculateDutyDistribution = (changes) => {
    const distribution = new Map();
    changes
      ? changes.map((ph) => {
          const diff = Number.isNaN(ph.currDay) ? 0 : ph.currDay;
          if (!distribution.has(diff)) distribution.set(diff, 0);
          distribution.set(diff, distribution.get(diff) + 1);
        })
      : 0;
    const list = new Array();
    distribution.forEach((value, key) => {
      list.push({ key, value });
    });
    const sortedList = list.sort((a, b) => a.key - b.key);
    return sortedList;
  };

  calculateDayDutyDistribution = (changes) => {
    const distribution = new Map();
    changes.map((ph) => {
      const diff = Number.isNaN(ph.currDay - ph.currNight) ? 0 : ph.currDay - ph.currNight;
      if (!distribution.has(diff)) distribution.set(diff, 0);
      distribution.set(diff, distribution.get(diff) + 1);
    });
    const list = new Array();
    distribution.forEach((value, key) => {
      list.push({ key, value });
    });
    const sortedList = list.sort((a, b) => a.key - b.key);
    return sortedList;
  };

  calculateNightDutyDistribution = (changes) => {
    const distribution = new Map();
    changes.map((ph) => {
      const diff = Number.isNaN(ph.currNight) ? 0 : ph.currNight;
      if (!distribution.has(diff)) distribution.set(diff, 0);
      distribution.set(diff, distribution.get(diff) + 1);
    });
    const list = new Array();
    distribution.forEach((value, key) => {
      list.push({ key, value });
    });
    const sortedList = list.sort((a, b) => a.key - b.key);
    return sortedList;
  };

  calculateDeltaDistribution = (changes) => {
    const distribution = new Map();
    changes.map((ph) => {
      const diff = Number.isNaN(ph.currDay - ph.prevDay) ? 0 : ph.currDay - ph.prevDay;
      if (!distribution.has(diff)) distribution.set(diff, 0);
      distribution.set(diff, distribution.get(diff) + 1);
    });
    const list = new Array();
    distribution.forEach((value, key) => {
      list.push({ key, value });
    });
    const sortedList = list.sort((a, b) => a.key - b.key);
    return sortedList;
  };

  calculateDeltaDayDistribution = (changes) => {
    const distribution = new Map();
    changes.map((ph) => {
      const diff = Number.isNaN(ph.currDay - ph.currNight - (ph.prevDay - ph.prevNight))
        ? 0
        : ph.currDay - ph.currNight - (ph.prevDay - ph.prevNight);
      if (!distribution.has(diff)) distribution.set(diff, 0);
      distribution.set(diff, distribution.get(diff) + 1);
    });
    const list = new Array();
    distribution.forEach((value, key) => {
      list.push({ key, value });
    });
    const sortedList = list.sort((a, b) => a.key - b.key);
    return sortedList;
  };

  calculateDeltaNightDistribution = (changes) => {
    const distribution = new Map();
    changes.map((ph) => {
      const diff = Number.isNaN(ph.currNight - ph.prevNight) ? 0 : ph.currNight - ph.prevNight;
      if (!distribution.has(diff)) distribution.set(diff, 0);
      distribution.set(diff, distribution.get(diff) + 1);
    });
    const list = new Array();
    distribution.forEach((value, key) => {
      list.push({ key, value });
    });
    const sortedList = list.sort((a, b) => a.key - b.key);
    return sortedList;
  };

  calculateFridayDistribution = (changes) => {
    const distribution = new Map();
    changes.map((ph) => {
      const diff = Number.isNaN(ph.fridayCnt) ? 0 : ph.fridayCnt;
      if (!distribution.has(diff)) distribution.set(diff, 0);
      distribution.set(diff, distribution.get(diff) + 1);
    });
    const list = new Array();
    distribution.forEach((value, key) => {
      list.push({ key, value });
    });
    const sortedList = list.sort((a, b) => a.key - b.key);
    return sortedList;
  };

  calculateSaturdayDistribution = (changes) => {
    const distribution = new Map();
    changes.map((ph) => {
      const diff = Number.isNaN(ph.saturdayCnt) ? 0 : ph.saturdayCnt;
      if (!distribution.has(diff)) distribution.set(diff, 0);
      distribution.set(diff, distribution.get(diff) + 1);
    });
    const list = new Array();
    distribution.forEach((value, key) => {
      list.push({ key, value });
    });
    const sortedList = list.sort((a, b) => a.key - b.key);
    return sortedList;
  };

  calculateSundayDistribution = (changes) => {
    const distribution = new Map();
    changes.map((ph) => {
      const diff = Number.isNaN(ph.sundayCnt) ? 0 : ph.sundayCnt;
      if (!distribution.has(diff)) distribution.set(diff, 0);
      distribution.set(diff, distribution.get(diff) + 1);
    });
    const list = new Array();
    distribution.forEach((value, key) => {
      list.push({ key, value });
    });
    const sortedList = list.sort((a, b) => a.key - b.key);
    return sortedList;
  };

  calculateWeekendDistribution = (changes) => {
    const distribution = new Map();
    changes.map((ph) => {
      let diff = Number.isNaN(ph.saturdayCnt) ? 0 : ph.saturdayCnt;
      diff = Number.isNaN(ph.sundayCnt) ? 0 : ph.sundayCnt;
      if (!distribution.has(diff)) distribution.set(diff, 0);
      distribution.set(diff, distribution.get(diff) + 1);
    });
    const list = new Array();
    distribution.forEach((value, key) => {
      list.push({ key, value });
    });
    const sortedList = list.sort((a, b) => a.key - b.key);
    return sortedList;
  };

  calculateBankholidayDistribution = (changes) => {
    const distribution = new Map();
    changes.map((ph) => {
      const diff = Number.isNaN(ph.holidayCnt) ? 0 : ph.holidayCnt;
      if (!distribution.has(diff)) distribution.set(diff, 0);
      distribution.set(diff, distribution.get(diff) + 1);
    });
    const list = new Array();
    distribution.forEach((value, key) => {
      list.push({ key, value });
    });
    const sortedList = list.sort((a, b) => a.key - b.key);
    return sortedList;
  };

  calculatePrevWeekendDistribution = (changes) => {
    const distribution = new Map();
    changes.map((ph) => {
      let diff = Number.isNaN(ph.saturdayCnt) ? 0 : ph.saturdayCnt;
      diff = Number.isNaN(ph.sundayCnt) ? 0 : ph.sundayCnt;
      if (!distribution.has(diff)) distribution.set(diff, 0);
      distribution.set(diff, distribution.get(diff) + 1);
    });
    const list = new Array();
    distribution.forEach((value, key) => {
      list.push({ key, value });
    });
    const sortedList = list.sort((a, b) => a.key - b.key);
    return sortedList;
  };

  calculateRatioDistribution = (changes) => {
    const distribution = new Map();
    changes.map((ph) => {
      const diff =
        Number.isNaN(ph.currDay) || ph.currDay == 0 ? 'n/a' : ((ph.currDay - ph.currNight) / ph.currDay).toFixed(2);
      if (!distribution.has(diff)) distribution.set(diff, 0);
      distribution.set(diff, distribution.get(diff) + 1);
    });
    const list = new Array();
    distribution.forEach((value, key) => {
      if (key != 'n/a') {
        list.push({ key, value });
      }
    });
    const sortedList = list.sort((a, b) => parseFloat(a.key) - parseFloat(b.key));
    return sortedList;
  };

  calculatePrevRatioDistribution = (changes) => {
    const distribution = new Map();
    changes.map((ph) => {
      const diff =
        Number.isNaN(ph.prevDay) || ph.prevDay == 0 ? 'n/a' : ((ph.prevDay - ph.prevNight) / ph.prevDay).toFixed(2);
      if (!distribution.has(diff)) distribution.set(diff, 0);
      distribution.set(diff, distribution.get(diff) + 1);
    });
    const list = new Array();
    distribution.forEach((value, key) => {
      if (key != 'n/a') {
        list.push({ key, value });
      }
    });
    const sortedList = list.sort((a, b) => parseFloat(a.key) - parseFloat(b.key));
    return sortedList;
  };

  detectGroupRuleTransgressions = async () => {
    this._isMounted && this.setState({ isLoading: true });
    const { MatchStore } = this.props;
    await MatchStore!.DetectGroupRuleTransgressions(
      this.state.savedPlanningId,
      this.state.selectedStartRuleTransgressionDate,
      this.state.selectedEndRuleTransgressionDate,
    );
    this._isMounted && this.setState({ isLoading: false });
  };

  search = async () => {
    if (this.state.isLoading) return;
    const { MatchStore } = this.props;
    this._isMounted && this.setState({ isLoading: true });
    const params = {
      offset: this.state.offset,
      status: this.state.status,
      savedPlanningId: this.state.savedPlanningId,
      savedPrevPlanningId: this.state.savedPrevPlanningId,
    };
    await Promise.all([
      MatchStore!.GetTooCloseDuties(
        this.state.startDate,
        this.state.endDate,
        this.state.lowerGapLimit,
        this.state.upperGapLimit,
        { offset: this.state.offset, savedPlanningId: this.state.savedPlanningId },
      ),
      MatchStore!.GetAbnormalDutyCounts(this.state.startDate, this.state.endDate, {
        offset: this.state.offset,
        savedPlanningId: this.state.savedPlanningId,
      }),
      MatchStore!.GetDutyCountChanges(this.state.startDate, this.state.endDate, params),
      MatchStore!.GetBlackpointTransgressions(this.state.startDate, this.state.endDate, {
        offset: this.state.offset,
        status: this.state.status,
      }),
      MatchStore!.GetGroupTransgressions(this.state.planningId, '*', {}),
      MatchStore!.GetGroupTransgressionsByDate(
        this.state.startDate,
        this.state.endDate,
        this.state.planningId,
        '*',
        {},
      ),
      MatchStore!.GetGroupAverageDutyCounts(this.state.startDate, this.state.endDate, this.state.savedPlanningId, {}),
      MatchStore!.GetUnbalancedNeighbors(this.state.startDate, this.state.endDate, {
        offset: this.state.offset,
        savedPlanningId: this.state.savedPlanningId,
      }),
    ]);
    this._isMounted && this.setState({ isLoading: false });
  };

  handleOffsetValueChange = (_valueAsNumber: number, _valueAsString: string) => {
    this.setState({ offset: _valueAsNumber });
    this.props.UiStore!.setDiagnosticOffset(_valueAsNumber);
  };

  handlePlanningIdValueChange = (_valueAsNumber: number, _valueAsString: string) => {
    this.setState({ savedPlanningId: _valueAsNumber });
    this.props.UiStore!.setDiagnosticPageCurrentPlanningId(_valueAsNumber);
  };

  handleSelectedPlanningIdValueChange = (_valueAsNumber: any, _valueAsString: string) => {
    this.setState({ savedPlanningId: _valueAsNumber.value, selectedPlanningId: _valueAsNumber });
    this.props.UiStore!.setDiagnosticPageCurrentPlanningId(_valueAsNumber.value);
    const planning = this.props.ScoreStore!.getById(_valueAsNumber.value);
    this.setStartDate(new Date(planning!.startDate!)); // semester.beginningStr));
    this.setEndDate(new Date(planning!.endDate!)); // semester.endStr));
  };

  handlePrevPlanningIdValueChange = (_valueAsNumber: number, _valueAsString: string) => {
    this.setState({ savedPrevPlanningId: _valueAsNumber });
    this.props.UiStore!.setDiagnosticPagePrevPlanningId(_valueAsNumber);
  };

  handleSemesterYearValueChange = (_valueAsNumber: number, _valueAsString: string) => {
    this.setState({ semesterYear: _valueAsNumber });
    this.props.UiStore!.setDiagnosticPageCurrentSemesterYear(_valueAsNumber);
  };

  handleStatusValueChange = (data: any) => {
    this.setState({ status: data });
    this.props.UiStore!.setDiagnosticSearchStatus(data);
  };

  buildSelectableScoreList = (scoreList: any[]) => {
    const list = Array.from(scoreList, (x) => {
      if (x.status === 'generated') {
        const obj = { label: x.planningId, value: x.planningId };
        return obj;
      } else return null;
    })
      .filter((x) => x != null)
      .sort((a, b) => (a!.value < b!.value ? 1 : -1));
    return list;
  };

  public render() {
    const { ScoreStore, MatchStore, IndictmentStore } = this.props;

    const scoreList = this.notReadyYet() ? {} : ScoreStore!.dataList;

    const selectableScoreList = this.notReadyYet() ? {} : this.buildSelectableScoreList(ScoreStore!.dataList);

    const indictmentList = this.state.isLoading ? {} : IndictmentStore!.dataList;
    const indictmentCountList = this.state.isLoading ? {} : IndictmentStore!.indictmentCount;
    const matchList = this.notReadyYet() ? {} : MatchStore!.miscDatas!['getComparedMatches'];
    const GetTooCloseDutiesList = this.notReadyYet() ? {} : MatchStore!.miscDatas!['GetTooCloseDuties'];
    const GetAbnormalDutyCountsList = this.notReadyYet() ? {} : MatchStore!.miscDatas!['GetAbnormalDutyCounts'];
    const GetDutyCountChangesList = this.notReadyYet() ? [] : MatchStore!.miscDatas!['GetDutyCountChanges'];
    const GetBlackpointTransgressionList = this.notReadyYet()
      ? []
      : MatchStore!.miscDatas!['GetBlackpointTransgressions'];
    const GetGroupTransgressionList = this.notReadyYet() ? [] : MatchStore!.miscDatas!['GetGroupTransgressions'];
    const GetGroupTransgressionByDateList = this.notReadyYet()
      ? []
      : MatchStore!.miscDatas!['GetGroupTransgressionsByDate'];
    const GetUnbalancedNeighborsList = this.notReadyYet() ? [] : MatchStore!.miscDatas!['GetUnbalancedNeighbors'];
    const DetectGroupRuleTransgressionList = this.notReadyYet()
      ? []
      : MatchStore!.miscDatas!['DetectGroupRuleTransgressions'];
    const GetGroupAverageDutyCountList = this.notReadyYet() ? [] : MatchStore!.miscDatas!['GetGroupAverageDutyCounts'];
    const finalDutyCountChangesList = GetDutyCountChangesList
      ? this.getFinalDutyCountChangesList(GetDutyCountChangesList)
      : [];
    const finalBlackpointTransgressionList = GetBlackpointTransgressionList
      ? this.getFinalBlackpointTransgressionList(GetBlackpointTransgressionList)
      : [];
    const finalGroupTransgressionList = GetGroupTransgressionList
      ? this.getFinalGroupTransgressionList(GetGroupTransgressionList)
      : [];
    const finalGroupTransgressionByDateList = GetGroupTransgressionByDateList
      ? this.getFinalGroupTransgressionByDateList(GetGroupTransgressionByDateList)
      : [];
    const finalUnbalancedNeighborsList = GetUnbalancedNeighborsList
      ? this.getUnbalancedNeighborsList(GetUnbalancedNeighborsList)
      : [];

    const defaultPlanning = { scoreId: 0, createdOn: 0, hardScore: 0, mediumScore: 0, softScore: 0 };
    const currentScore = this.notReadyYet() ? defaultPlanning : ScoreStore!.getById(this.state.planningId);
    const previousScore = this.notReadyYet() ? defaultPlanning : ScoreStore!.getById(this.state.planningId - 1);
    const nextScore = this.notReadyYet() ? defaultPlanning : ScoreStore!.getById(this.state.planningId + 1);
    const dutyDistribution = GetDutyCountChangesList ? this.calculateDutyDistribution(GetDutyCountChangesList) : [];
    const dayDutyDistribution = GetDutyCountChangesList
      ? this.calculateDayDutyDistribution(GetDutyCountChangesList)
      : [];
    const nightDutyDistribution = GetDutyCountChangesList
      ? this.calculateNightDutyDistribution(GetDutyCountChangesList)
      : [];
    const deltaDistribution = GetDutyCountChangesList ? this.calculateDeltaDistribution(GetDutyCountChangesList) : [];
    const deltaDayDistribution = GetDutyCountChangesList
      ? this.calculateDeltaDayDistribution(GetDutyCountChangesList)
      : [];
    const deltaNightDistribution = GetDutyCountChangesList
      ? this.calculateDeltaNightDistribution(GetDutyCountChangesList)
      : [];
    const ratioDistribution = GetDutyCountChangesList ? this.calculateRatioDistribution(GetDutyCountChangesList) : [];
    const prevRatioDistribution = GetDutyCountChangesList
      ? this.calculatePrevRatioDistribution(GetDutyCountChangesList)
      : [];
    const fridayDistribution = finalUnbalancedNeighborsList
      ? this.calculateFridayDistribution(finalUnbalancedNeighborsList)
      : [];
    const saturdayDistribution = finalUnbalancedNeighborsList
      ? this.calculateSaturdayDistribution(finalUnbalancedNeighborsList)
      : [];
    const sundayDistribution = finalUnbalancedNeighborsList
      ? this.calculateSundayDistribution(finalUnbalancedNeighborsList)
      : [];
    const weekendDistribution = finalUnbalancedNeighborsList
      ? this.calculateWeekendDistribution(finalUnbalancedNeighborsList)
      : [];
    const bankHolidayDistribution = finalUnbalancedNeighborsList
      ? this.calculateBankholidayDistribution(finalUnbalancedNeighborsList)
      : [];

    const selectedDate = this.state.selectedDate;
    const offset = this.state.offset;
    const savedPlanningId = this.state.savedPlanningId;
    const selectedPlanningId = this.state.selectedPlanningId;
    const savedPrevPlanningId = this.state.savedPrevPlanningId;
    const selectedStartRuleTransgressionDate = this.state.selectedStartRuleTransgressionDate;
    const selectedEndRuleTransgressionDate = this.state.selectedEndRuleTransgressionDate;
    const year = this.state.semesterYear;

    return (
      <MainSectionLayout isLoading={this.notReadyYet()} title={TLCode.ANALYSIS_MAIN_TITLE}>
        {this.notReadyYet() ? (
          <Container />
        ) : (
          <Container>
            <Card className={CardStyle.toString()}>
              <Tabs
                id="main"
                renderActiveTabPanelOnly={true}
                defaultSelectedTabId={TAB.PROBLEMS}
                selectedTabId={this.state.selectedTabId}
                onChange={this.handleTabChange}
              >
                <Tab
                  id={TAB.PROBLEMS}
                  title={this.props.translate(TLCode.ANALYSIS_PROBLEMS)}
                  panel={
                    <span>
                      <div>
                        <span>
                          <DivLine>
                            <DivColumn>
                              <DivLine>{this.props.translate(TLCode.ANALYSIS_CLICK_SEMESTER)}</DivLine>
                              <DivLine>
                                {this.props.translate(TLCode.ROSTER_Year)}&nbsp;
                                <NumericInput
                                  style={{ width: '60px' }}
                                  min={2016}
                                  value={year}
                                  onValueChange={this.handleSemesterYearValueChange}
                                />
                                <ButtonGroup>
                                  <Button intent="primary" onClick={this.setS1}>
                                    <TranslatedMessage id={TLCode.ROSTER_S1} />
                                  </Button>
                                  &nbsp;&nbsp;
                                  <Button intent="primary" onClick={this.setS2}>
                                    <TranslatedMessage id={TLCode.ROSTER_S2} />
                                  </Button>
                                </ButtonGroup>
                              </DivLine>
                              <DivLine>
                                <DivColumn style={{ width: '80px' }}>
                                  {this.props.translate(TLCode.ANALYSIS_PLANNINGID)}&nbsp;
                                </DivColumn>
                                <DivColumn style={{ width: '100px' }}>
                                  {/* <NumericInput style={{ width: '60px' }} min={-1} value={savedPlanningId} onValueChange={this.handlePlanningIdValueChange} /> */}
                                  <Select
                                    options={selectableScoreList}
                                    value={selectedPlanningId}
                                    onChange={this.handleSelectedPlanningIdValueChange}
                                  />
                                </DivColumn>
                              </DivLine>
                            </DivColumn>
                            <DivColumn>
                              <DivLine>{this.props.translate(TLCode.ANALYSIS_PERIOD)}</DivLine>
                              <DivLine>
                                <span style={{ width: '100px' }}>
                                  <DateInput
                                    value={selectedDate}
                                    canClearSelection={false}
                                    onChange={(value) => {
                                      this.selectedDateChange(value);
                                    }}
                                  />
                                </span>
                              </DivLine>
                              <DivLine>-</DivLine>
                            </DivColumn>
                            <DivColumn>
                              <DivLine>{this.props.translate(TLCode.ANALYSIS_START)}</DivLine>
                              <DivLine>
                                <span style={{ width: '100px' }}>
                                  <DateInput value={this.state.startDate} disabled={true} />
                                </span>
                              </DivLine>
                              <DivLine>-</DivLine>
                            </DivColumn>
                            <DivColumn>
                              <DivLine>{this.props.translate(TLCode.ANALYSIS_END)}</DivLine>
                              <DivLine>
                                <span style={{ width: '100px' }}>
                                  <DateInput value={this.state.endDate} disabled={true} />
                                </span>
                              </DivLine>
                              <DivLine>-</DivLine>
                            </DivColumn>
                            <DivColumn>
                              <DivLine>
                                {this.props.translate(TLCode.ANALYSIS_OFFSET)}&nbsp;
                                {/* <DivColumn1> */}
                                <NumericInput
                                  style={{ width: '60px' }}
                                  value={offset}
                                  onValueChange={this.handleOffsetValueChange}
                                />
                                {/* </DivColumn1> */}
                              </DivLine>
                              <DivLine>
                                {this.props.translate(TLCode.ANALYSIS_PREVPLANNINGID)}&nbsp;
                                <NumericInput
                                  style={{ width: '60px' }}
                                  min={-1}
                                  value={savedPrevPlanningId}
                                  onValueChange={this.handlePrevPlanningIdValueChange}
                                />
                              </DivLine>
                            </DivColumn>
                            <DivColumn>
                              <DivLine>
                                <div style={{ width: '40px' }}>
                                  {this.props.translate(TLCode.ANALYSIS_STATUS)}&nbsp;
                                </div>
                                <div style={{ width: '120px' }}>
                                  <Select
                                    options={[
                                      { label: 'live', value: 'live' },
                                      { label: 'publié', value: 'published' },
                                      { label: 'généré', value: 'generated' },
                                    ]}
                                    defaultValue={{ label: 'publié', value: 'published' }}
                                    value={this.state.status}
                                    onChange={this.handleStatusValueChange}
                                  />
                                </div>
                              </DivLine>
                            </DivColumn>
                            <DivColumn>
                              <Button intent="primary" icon={IconNames.CALCULATOR} onClick={this.search}>
                                <TranslatedMessage id={TLCode.SECRETARIAT_DASHBOARD_ANNUAL_REVIEW_Calculate} />
                              </Button>
                            </DivColumn>
                            {/* <DivColumn>
                            <DivLine>
                              {this.props.translate(TLCode.ANALYSIS_START)}&nbsp;
                              <div style={{ width: '100px' }}>
                                <DateInput value={this.state.startDate} canClearSelection={false} disabled/>
                                </div>
                             &nbsp;{this.props.translate(TLCode.ANALYSIS_END)}&nbsp;
                              <div style={{ width: '100px' }}>
                              <DateInput value={this.state.endDate} canClearSelection={false} disabled/>
                              </div>
                            </DivLine>
                          </DivColumn> */}
                          </DivLine>
                        </span>
                        {/* <Form
                          initialValues={{ searchCriteria: searchCriteria }}
                          onSubmit={(data: any) => console.log('data', data)}
                          render={({ handleSubmit, form, hasValidationErrors, dirty, values }) => (
                            <form onSubmit={handleSubmit}>
                              <DivLine>
                              <DateInputFormField
                                name="startDate"
                                label={TLCode.ANALYSIS_PERIOD}
                                inline={true}
                                fill={true}
                                maxDate={moment()
                                  .add(20, 'year')
                                  .toDate()}
                              />
                              <DateInputFormField
                                name="endDate"
                                label={TLCode.ANALYSIS_PERIOD_UNTIL}
                                inline={true}
                                fill={true}
                                maxDate={moment()
                                  .add(20, 'year')
                                  .toDate()}
                              />
                              <Button type={'submit'} intent="primary" icon={IconNames.CALCULATOR} onClick={this.search}>
                                <TranslatedMessage id={TLCode.SECRETARIAT_DASHBOARD_ANNUAL_REVIEW_Calculate} />
                              </Button>
                              </DivLine>
                            </form>
                          )}
                        /> */}

                        <br />
                        <p>
                          <b>
                            {this.props.translate(TLCode.ANALYSIS_GLOBAL_PERF)} {this.getGlobalPerformancePercentage()}%
                          </b>{' '}
                          {this.props.translate(TLCode.ANALYSIS_GLOBAL_PERF_COMMENT)}
                        </p>
                      </div>
                      {/* <br /> */}
                      <Tabs
                        id="sub"
                        renderActiveTabPanelOnly={true}
                        defaultSelectedTabId={SUBTAB.TOOCLOSEDUTIES}
                        selectedTabId={this.state.selectedSubTabId}
                        onChange={this.handleSubTabChange}
                      >
                        <Tab
                          id={SUBTAB.TOOCLOSEDUTIES}
                          title={this.props.translate(TLCode.TOO_CLOSE_DUTIES)}
                          panel={
                            <span>
                              <p>
                                <b>Planning {savedPlanningId > -1 ? savedPlanningId : 'live'}</b>&nbsp;&nbsp;&nbsp;
                                <b>
                                  {this.props.translate(TLCode.TOO_CLOSE_DUTIES_DETAILS1)}[{this.state.lowerGapLimit}
                                  &nbsp;-&nbsp;{this.state.upperGapLimit}]&nbsp;&nbsp;&nbsp;
                                </b>
                                <b>
                                  {this.props.translate(TLCode.ANALYSIS_PERF)}
                                  {MatchStore!.miscDatas['GetTooCloseDutiesPerformance']}%&nbsp;&nbsp;&nbsp;
                                </b>
                                {MatchStore!.miscDatas['GetTooCloseDutiesCount']}
                                {this.props.translate(TLCode.ON_A_TOTAL_OF)}
                                {MatchStore!.miscDatas['GetTooCloseDutiesMaximum']}
                                {this.props.translate(TLCode.DUTIES)}(
                                {MatchStore!.miscDatas['GetTooCloseDutiesPercentage']}%)
                              </p>
                              <p>{this.props.translate(TLCode.PHARMACY_LINKS_NOTICE)}</p>
                              <BaseGridXL
                                height="600px"
                                keyName="rowid"
                                columnDefs={this.GetTooCloseDutiesColumns}
                                rowData={GetTooCloseDutiesList}
                                loading={this.notReadyYet()}
                                onRowDoubleClicked={this.onPharmacySelected}
                                exportName="PharmagardeTooCloseDutyList"
                              />
                            </span>
                          }
                        />
                        <Tab
                          id={SUBTAB.ABNORMALDUTYCOUNTS}
                          title={this.props.translate(TLCode.ABNORMAL_DUTY_COUNTS)}
                          panel={
                            <span>
                              <p>
                                <b>
                                  <b>Planning {savedPlanningId > -1 ? savedPlanningId : 'live'}</b>&nbsp;&nbsp;&nbsp;
                                  {this.props.translate(TLCode.ABNORMAL_DUTY_COUNTS_DETAILS)}
                                  {/* ( &lt; */}
                                  {/* {MatchStore!.miscDatas['GetAbnormalDutyCountsMinForPeriod']}
                                  {this.props.translate(TLCode.OR)}&gt;
                                  {MatchStore!.miscDatas['GetAbnormalDutyCountsMaxForPeriod']}
                                  {' / '}
                                  {MatchStore!.miscDatas['GetAbnormalDutyCountsNumberOfDays']}
                                  {this.props.translate(TLCode.DAYS)}) */}
                                </b>
                                {/* <br /> */}&nbsp;&nbsp;
                                <b>
                                  {this.props.translate(TLCode.ANALYSIS_PERF)}
                                  {MatchStore!.miscDatas['GetAbnormalDutyCountsPerformance']}%&nbsp;&nbsp;&nbsp;
                                </b>
                                {/* </p>
                              <p></p>
                              <p> */}
                                {MatchStore!.miscDatas['GetAbnormalDutyCountsCount']}
                                {this.props.translate(TLCode.ON_A_TOTAL_OF)}
                                {MatchStore!.miscDatas['GetAbnormalDutyCountsNumberOfPharmacies']}
                                {this.props.translate(TLCode.PHARMACIES)}(
                                {MatchStore!.miscDatas['GetAbnormalDutyCountsPercentage']}%)
                                {this.props.translate(TLCode.AMONG_WHICH)}
                                {MatchStore!.miscDatas['GetAbnormalDutyCountsNumberOfLevel3Pharmacies']}
                                {this.props.translate(TLCode.PHARMACIES)} {this.props.translate(TLCode.LEVEL3)}(
                                {MatchStore!.miscDatas['GetAbnormalDutyCountsLevel3Percentage']}%)
                              </p>
                              <p>{this.props.translate(TLCode.PHARMACY_LINKS_NOTICE)}</p>
                              <BaseGridXL
                                height="600px"
                                keyName="rowid"
                                columnDefs={this.GetAbnormalDutyCountsColumns}
                                rowData={GetAbnormalDutyCountsList}
                                loading={this.notReadyYet()}
                                onRowDoubleClicked={this.onPharmacySelected}
                                exportName="PharmagardeDutyCountList"
                              />
                              <br />
                              <br />
                              <DivLine>
                                <LineChart
                                  width={500}
                                  height={250}
                                  data={dutyDistribution}
                                  margin={{
                                    top: 5,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                  }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="key" tpe="number" interval={0} />
                                  <YAxis>
                                    <Label value="Phies" position="insideLeft" offset={-10} />
                                  </YAxis>
                                  <Tooltip />
                                  <Legend />
                                  <Line
                                    type="monotone"
                                    dataKey="value"
                                    name="Distribution nbre total de gardes par pharmacie"
                                    stroke="#8884d8"
                                    activeDot={{ r: 8 }}
                                  />
                                  <ChartTooltip />
                                </LineChart>
                                {/* </DivLine>
                              <DivLine> */}
                                <LineChart
                                  width={500}
                                  height={250}
                                  data={dayDutyDistribution}
                                  margin={{
                                    top: 5,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                  }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="key" tpe="number" interval={0} />
                                  <YAxis>
                                    <Label value="Phies" position="insideLeft" offset={-10} />
                                  </YAxis>
                                  <Tooltip />
                                  <Legend />
                                  <Line
                                    type="monotone"
                                    dataKey="value"
                                    name="Distribution nbre de gardes de jour par pharmacie"
                                    stroke="#8884d8"
                                    activeDot={{ r: 8 }}
                                  />
                                  <ChartTooltip />
                                </LineChart>
                                {/* </DivLine>
                              <DivLine> */}
                                <LineChart
                                  width={500}
                                  height={250}
                                  data={nightDutyDistribution}
                                  margin={{
                                    top: 5,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                  }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="key" tpe="number" interval={0} />
                                  <YAxis>
                                    <Label value="Phies" position="insideLeft" offset={-10} />
                                  </YAxis>
                                  <Tooltip />
                                  <Legend />
                                  <Line
                                    type="monotone"
                                    dataKey="value"
                                    name="Distribution nbre de gardes de nuit par pharmacie"
                                    stroke="#8884d8"
                                    activeDot={{ r: 8 }}
                                  />
                                  <ChartTooltip />
                                </LineChart>
                              </DivLine>
                            </span>
                          }
                        />
                        <Tab
                          id={SUBTAB.DUTYCOUNTCHANGES}
                          title={this.props.translate(TLCode.DUTY_COUNT_CHANGES_TITLE)}
                          panel={
                            <span>
                              <p>
                                <b>Planning live</b>&nbsp;&nbsp;&nbsp;
                                {/* <p><b>Planning {savedPlanningId>-1?savedPlanningId:'live'}</b>&nbsp;&nbsp;&nbsp; */}
                                <b>Total:</b> {this.calculateCurrDutyTotal(GetDutyCountChangesList)}&nbsp;&nbsp;&nbsp;
                                <b>préc.</b> {this.calculatePrevDutyTotal(GetDutyCountChangesList)}&nbsp;&nbsp;<b>Δ</b>{' '}
                                {this.calculateCurrDutyTotal(GetDutyCountChangesList) -
                                  this.calculatePrevDutyTotal(GetDutyCountChangesList)}
                                &nbsp;&nbsp;&nbsp;&nbsp;---
                                <b>Jours seuls:</b> {this.calculateCurrDayDutyTotal(GetDutyCountChangesList)}
                                &nbsp;&nbsp;&nbsp;<b>préc.</b> {this.calculatePrevDayDutyTotal(GetDutyCountChangesList)}
                                &nbsp;&nbsp;<b>Δ</b>{' '}
                                {this.calculateCurrDayDutyTotal(GetDutyCountChangesList) -
                                  this.calculatePrevDayDutyTotal(GetDutyCountChangesList)}
                                &nbsp;&nbsp;&nbsp;&nbsp;---
                                <b>Jours + Nuits:</b> {this.calculateCurrNightDutyTotal(GetDutyCountChangesList)}
                                &nbsp;&nbsp;&nbsp;<b>préc.</b>{' '}
                                {this.calculatePrevNightDutyTotal(GetDutyCountChangesList)}&nbsp;&nbsp;<b>Δ</b>{' '}
                                {this.calculateCurrNightDutyTotal(GetDutyCountChangesList) -
                                  this.calculatePrevNightDutyTotal(GetDutyCountChangesList)}
                              </p>
                              <p>
                                {this.props.translate(TLCode.DUTY_COUNT_CHANGES_EXPLANATION_1)}&nbsp;&nbsp;&nbsp;
                                {this.props.translate(TLCode.DUTY_COUNT_CHANGES_EXPLANATION_2)}
                              </p>
                              <p>{this.props.translate(TLCode.PHARMACY_LINKS_NOTICE)}</p>
                              <Tabs
                                id="subDutyCountChanges"
                                renderActiveTabPanelOnly={true}
                                defaultSelectedTabId={SUBSUBTAB.DUTYCOUNTCHANGESGRAPHS}
                                selectedTabId={this.state.selectedDutyCountSubTabId}
                                onChange={this.handleSubSubTabChange}
                              >
                                <Tab
                                  id={SUBSUBTAB.DUTYCOUNTCHANGESGRID}
                                  title={this.props.translate(TLCode.DUTY_COUNT_CHANGES_LIST_TITLE)}
                                  panel={
                                    <BaseGridXL
                                      height="600px"
                                      keyName="rowid"
                                      columnDefs={this.GetDutyCountChangesColumns}
                                      rowData={finalDutyCountChangesList}
                                      loading={this.notReadyYet()}
                                      onRowDoubleClicked={this.onPharmacyIdSelected}
                                      exportName="PharmagardeDutyCountChanges"
                                    />
                                  }
                                ></Tab>
                                <Tab
                                  id={SUBSUBTAB.DUTYCOUNTCHANGESGRAPHS}
                                  title={this.props.translate(TLCode.DUTY_COUNT_CHANGES_GRAPHS_TITLE)}
                                  panel={
                                    <>
                                      <DivLine>
                                        <LineChart
                                          width={500}
                                          height={250}
                                          data={deltaDistribution}
                                          margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                          }}
                                        >
                                          <CartesianGrid strokeDasharray="3 3" />
                                          <XAxis dataKey="key" tpe="number" interval={0}></XAxis>
                                          <YAxis>
                                            <Label value="Phies" position="insideLeft" offset={-10} />
                                          </YAxis>
                                          <ReferenceLine x="0" stroke="red" />
                                          <Tooltip />
                                          <Legend />
                                          <Line
                                            type="monotone"
                                            dataKey="value"
                                            name="Différence nbre total de gardes"
                                            stroke="#8884d8"
                                            activeDot={{ r: 8 }}
                                          />
                                          <ChartTooltip />
                                        </LineChart>
                                        <LineChart
                                          width={500}
                                          height={250}
                                          data={deltaDayDistribution}
                                          margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                          }}
                                        >
                                          <CartesianGrid strokeDasharray="3 3" />
                                          <XAxis dataKey="key" tpe="number" interval={0}></XAxis>
                                          <YAxis>
                                            <Label value="Phies" position="insideLeft" offset={-10} />
                                          </YAxis>
                                          <ReferenceLine x="0" stroke="red" />
                                          <Tooltip />
                                          <Legend />
                                          <Line
                                            type="monotone"
                                            dataKey="value"
                                            name="Différence nbre de gardes de jour"
                                            stroke="#8884d8"
                                            activeDot={{ r: 8 }}
                                          />
                                          <ChartTooltip />
                                        </LineChart>
                                        <LineChart
                                          width={500}
                                          height={250}
                                          data={deltaNightDistribution}
                                          margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                          }}
                                        >
                                          <CartesianGrid strokeDasharray="3 3" />
                                          <XAxis dataKey="key" tpe="number" interval={0}></XAxis>
                                          <YAxis>
                                            <Label value="Phies" position="insideLeft" offset={-10} />
                                          </YAxis>
                                          <ReferenceLine x="0" stroke="red" />
                                          <Tooltip />
                                          <Legend />
                                          <Line
                                            type="monotone"
                                            dataKey="value"
                                            name="Différence nbre de gardes de nuit"
                                            stroke="#8884d8"
                                            activeDot={{ r: 8 }}
                                          />
                                          <ChartTooltip />
                                        </LineChart>
                                      </DivLine>
                                      <DivLine>
                                        <LineChart
                                          width={600}
                                          height={250}
                                          data={prevRatioDistribution}
                                          margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                          }}
                                        >
                                          <CartesianGrid strokeDasharray="3 3" />
                                          <XAxis dataKey="key" tpe="number" interval={0.5} domain={[0.0, 1.0]}></XAxis>
                                          <YAxis domain={[0, 90]}>
                                            <Label value="Phies" position="insideLeft" offset={-10} />
                                          </YAxis>
                                          <ReferenceLine x="0" stroke="red" />
                                          <Tooltip />
                                          <Legend />
                                          <Line
                                            type="monotone"
                                            dataKey="value"
                                            name="Ratio précédent jour seul/ total 0=> jamais de nuit, 1=> toujours nuit"
                                            stroke="#8884d8"
                                            activeDot={{ r: 8 }}
                                          />
                                          <ChartTooltip />
                                        </LineChart>
                                      </DivLine>
                                      <DivLine>
                                        <LineChart
                                          width={600}
                                          height={250}
                                          data={ratioDistribution}
                                          margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                          }}
                                        >
                                          <CartesianGrid strokeDasharray="3 3" />
                                          <XAxis dataKey="key" tpe="number" interval={0.5} domain={[0.0, 1.0]}></XAxis>
                                          <YAxis domain={[0, 90]}>
                                            <Label value="Phies" position="insideLeft" offset={-10} />
                                          </YAxis>
                                          <ReferenceLine x="0" stroke="red" />
                                          <Tooltip />
                                          <Legend />
                                          <Line
                                            type="monotone"
                                            dataKey="value"
                                            name="Ratio courant jour seul/ total 0=> jamais de nuit, 1=> toujours nuit"
                                            stroke="#8884d8"
                                            activeDot={{ r: 8 }}
                                          />
                                          <ChartTooltip />
                                        </LineChart>
                                      </DivLine>
                                    </>
                                  }
                                ></Tab>
                              </Tabs>
                            </span>
                          }
                        />
                        <Tab
                          id={SUBTAB.UNBALANCEDNEIGHBORS}
                          title={this.props.translate(TLCode.UNBALANCED_NEIGHBORS_TITLE)}
                          panel={
                            <span>
                              <p>
                                <b>Planning {savedPlanningId > -1 ? savedPlanningId : 'live'}</b>&nbsp;&nbsp;&nbsp;
                                {/* <b>
                                  {this.props.translate(TLCode.ANALYSIS_GLOBAL_PERF)}{' '}
                                  {this.getGlobalPerformancePercentage()}%
                                </b>{' '} */}
                                {this.props.translate(TLCode.UNBALANCED_NEIGHBORS_COMMENTS)}
                                {/* </p>
                              <p> */}&nbsp;&nbsp;&nbsp;
                                {this.props.translate(TLCode.PHARMACY_LINKS_NOTICE)}
                              </p>
                              {/* <BaseGridXL
                                height="600px"
                                keyName="rowid"
                                columnDefs={this.GetUnbalancedNeighborsColumns}
                                rowData={GetUnbalancedNeighborsList}
                                loading={this.notReadyYet()}
                                onRowDoubleClicked={this.onPharmacyIdSelected}
                                exportName={'ComparaisonNombreDeGardes-' + moment().format('YYYY-MM-DD')}
                              /> */}
                              {/* <FeaturedGrid
                                height="600px"
                                keyName="pharmacyId"
                                columnDefs={this.GetUnbalancedNeighborsColumns}
                                rowData={finalUnbalancedNeighborsList}
                                loading={this.notReadyYet()}
                                onRowDoubleClicked={this.onPharmacyIdSelected}
                                exportName={'ComparaisonNombreDeGardes-' + moment().format('YYYY-MM-DD')}
                              /> */}
                              <Tabs
                                id="subUnbalancedNeighbors"
                                renderActiveTabPanelOnly={true}
                                defaultSelectedTabId={SUBSUBTAB.UNBALANCEDNEIGHBORSGRAPHS}
                                selectedTabId={this.state.selectedUnbalancedCountSubTabId}
                                onChange={this.handleSubSubTabChange}
                              >
                                <Tab
                                  id={SUBSUBTAB.UNBALANCEDNEIGHBORSGRID}
                                  title={this.props.translate(TLCode.UNBALANCED_NEIGHBORS_LIST_TITLE)}
                                  panel={
                                    <BaseGridXL
                                      height="600px"
                                      keyName="pharmacyId"
                                      columnDefs={this.GetUnbalancedNeighborsColumns}
                                      rowData={finalUnbalancedNeighborsList}
                                      loading={this.notReadyYet()}
                                      onRowDoubleClicked={this.onPharmacyIdSelected}
                                      exportName="PharmagardDutyCountChanges"
                                    />
                                  }
                                ></Tab>
                                <Tab
                                  id={SUBSUBTAB.UNBALANCEDNEIGHBORSGRAPHS}
                                  title={this.props.translate(TLCode.UNBALANCED_NEIGHBORS_GRAPHS_TITLE)}
                                  panel={
                                    <>
                                      <DivLine>
                                        <LineChart
                                          width={350}
                                          height={200}
                                          data={fridayDistribution}
                                          margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                          }}
                                        >
                                          <CartesianGrid strokeDasharray="3 3" />
                                          <XAxis dataKey="key" tpe="number" interval={0}></XAxis>
                                          <YAxis>
                                            <Label value="Phies" position="insideLeft" offset={-10} />
                                          </YAxis>
                                          <ReferenceLine x="0" stroke="red" />
                                          <Tooltip />
                                          <Legend />
                                          <Line
                                            type="monotone"
                                            dataKey="value"
                                            name="Distribution nbre de gardes le vendredi"
                                            stroke="#8884d8"
                                            activeDot={{ r: 8 }}
                                          />
                                          <ChartTooltip />
                                        </LineChart>
                                        <LineChart
                                          width={350}
                                          height={200}
                                          data={saturdayDistribution}
                                          margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                          }}
                                        >
                                          <CartesianGrid strokeDasharray="3 3" />
                                          <XAxis dataKey="key" tpe="number" interval={0}></XAxis>
                                          <YAxis>
                                            <Label value="Phies" position="insideLeft" offset={-10} />
                                          </YAxis>
                                          <ReferenceLine x="0" stroke="red" />
                                          <Tooltip />
                                          <Legend />
                                          <Line
                                            type="monotone"
                                            dataKey="value"
                                            name="Distribution nbre de gardes le samedi"
                                            stroke="#8884d8"
                                            activeDot={{ r: 8 }}
                                          />
                                          <ChartTooltip />
                                        </LineChart>
                                        <LineChart
                                          width={350}
                                          height={200}
                                          data={sundayDistribution}
                                          margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                          }}
                                        >
                                          <CartesianGrid strokeDasharray="3 3" />
                                          <XAxis dataKey="key" tpe="number" interval={0}></XAxis>
                                          <YAxis>
                                            <Label value="Phies" position="insideLeft" offset={-10} />
                                          </YAxis>
                                          <ReferenceLine x="0" stroke="red" />
                                          <Tooltip />
                                          <Legend />
                                          <Line
                                            type="monotone"
                                            dataKey="value"
                                            name="Distribution nbre de gardes le dimanche"
                                            stroke="#8884d8"
                                            activeDot={{ r: 8 }}
                                          />
                                          <ChartTooltip />
                                        </LineChart>
                                      </DivLine>
                                      <DivLine>
                                        <LineChart
                                          width={350}
                                          height={200}
                                          data={weekendDistribution}
                                          margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                          }}
                                        >
                                          <CartesianGrid strokeDasharray="3 3" />
                                          <XAxis dataKey="key" tpe="number" interval={0}></XAxis>
                                          <YAxis>
                                            <Label value="Phies" position="insideLeft" offset={-10} />
                                          </YAxis>
                                          <ReferenceLine x="0" stroke="red" />
                                          <Tooltip />
                                          <Legend />
                                          <Line
                                            type="monotone"
                                            dataKey="value"
                                            name="Distribution nbre de gardes le weekend"
                                            stroke="#8884d8"
                                            activeDot={{ r: 8 }}
                                          />
                                          <ChartTooltip />
                                        </LineChart>
                                        <LineChart
                                          width={350}
                                          height={200}
                                          data={bankHolidayDistribution}
                                          margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                          }}
                                        >
                                          <CartesianGrid strokeDasharray="3 3" />
                                          <XAxis dataKey="key" tpe="number" interval={0}></XAxis>
                                          <YAxis>
                                            <Label value="Phies" position="insideLeft" offset={-10} />
                                          </YAxis>
                                          <ReferenceLine x="0" stroke="red" />
                                          <Tooltip />
                                          <Legend />
                                          <Line
                                            type="monotone"
                                            dataKey="value"
                                            name="Distribution nbre de gardes les jours fériés"
                                            stroke="#8884d8"
                                            activeDot={{ r: 8 }}
                                          />
                                          <ChartTooltip />
                                        </LineChart>
                                      </DivLine>
                                    </>
                                  }
                                ></Tab>
                              </Tabs>
                            </span>
                          }
                        />
                        <Tab
                          id={SUBTAB.GROUPANALYSIS}
                          title={this.props.translate(TLCode.GROUPANALYSIS_TITLE)}
                          panel={
                            <span>
                              <DivLine>
                                <DivColumn style={{ width: '1200px' }}>
                                  <DivLine>
                                    <DivColumn style={{ width: '600px' }}>
                                      <p>
                                        <b>{this.props.translate(TLCode.GROUPANALYSIS_GROUP_TRANSGRESSIONS_BY_DATE)}</b>
                                      </p>
                                      {GetGroupTransgressionByDateList != undefined && (
                                        <BaseGridXL
                                          height="600px"
                                          width="600px"
                                          keyName="rowid"
                                          columnDefs={this.GetGroupTransgressionsByDateColumns}
                                          rowData={finalGroupTransgressionByDateList}
                                          loading={this.notReadyYet()}
                                          // onRowDoubleClicked={this.onPharmacyIdSelected}
                                          exportName="PharmagardeGroupRuleTransgressions"
                                        />
                                      )}
                                    </DivColumn>
                                    <DivColumn style={{ width: '600px' }}>
                                      <p>
                                        <b>Planning {savedPlanningId > -1 ? savedPlanningId : 'live'}</b>
                                        <b>{this.props.translate(TLCode.GROUPANALYSIS_GROUP_AVERAGE_DUTY_COUNTS)}</b>
                                      </p>
                                      {GetGroupAverageDutyCountList != undefined && (
                                        <BaseGridXL
                                          height="600px"
                                          width="600px"
                                          keyName="rowid"
                                          columnDefs={this.GroupAverageDutyCountColumns}
                                          rowData={GetGroupAverageDutyCountList}
                                          loading={this.notReadyYet()}
                                          // onRowDoubleClicked={this.onPharmacyIdSelected}
                                          exportName="PharmagardeGroupRuleTransgressions"
                                        />
                                      )}
                                    </DivColumn>
                                  </DivLine>
                                  <br />
                                  <DivLine style={{ width: '600px' }}>
                                    <DivColumn style={{ width: '200px' }}>
                                      <DivLine>
                                        {this.props.translate(TLCode.StartDate)}
                                        <div style={{ width: '100px' }}>
                                          <DateInput
                                            value={selectedStartRuleTransgressionDate}
                                            canClearSelection={false}
                                            onChange={(value) => {
                                              this.selectedStartRuleTransgressionChange(value);
                                            }}
                                          />
                                        </div>
                                      </DivLine>
                                    </DivColumn>
                                    <DivColumn style={{ width: '200px' }}>
                                      <DivLine>
                                        {this.props.translate(TLCode.EndDate)}&nbsp;
                                        <div style={{ width: '100px' }}>
                                          <DateInput
                                            value={selectedEndRuleTransgressionDate}
                                            canClearSelection={false}
                                            onChange={(value) => {
                                              this.selectedEndRuleTransgressionChange(value);
                                            }}
                                          />
                                        </div>
                                      </DivLine>
                                    </DivColumn>
                                    <DivColumn style={{ width: '100px' }}>
                                      <Button
                                        intent="primary"
                                        icon={IconNames.CALCULATOR}
                                        onClick={this.detectGroupRuleTransgressions}
                                      >
                                        <TranslatedMessage id={TLCode.GROUPANALYSIS_CALCULATE} />
                                      </Button>
                                    </DivColumn>
                                  </DivLine>
                                  <DivLine>
                                    <DivColumn>
                                      <p>
                                        <b>Planning {savedPlanningId > -1 ? savedPlanningId : 'live'}</b>
                                        &nbsp;&nbsp;&nbsp;
                                        <b>{this.props.translate(TLCode.GROUPANALYSIS_DYNAMIC_TRANSGRESSIONS)}</b>
                                      </p>
                                      {DetectGroupRuleTransgressionList != undefined && (
                                        <BaseGridXL
                                          height="600px"
                                          width="600px"
                                          keyName="rowid"
                                          columnDefs={this.GroupRuleTransgressionColumns}
                                          rowData={DetectGroupRuleTransgressionList}
                                          loading={this.notReadyYet()}
                                          // onRowDoubleClicked={this.onPharmacyIdSelected}
                                          exportName="PharmagardeGroupRuleTransgressions"
                                        />
                                      )}
                                    </DivColumn>
                                  </DivLine>
                                </DivColumn>
                                {/* <DivColumn style={{ width: '600px' }}>
                                  <DivLine><br/></DivLine>
                                  <DivLine>
                                    <DivColumn>
                                      <p>
                                      <b>{this.props.translate(TLCode.SPECIALISSUES_GROUPTRANSGRESSIONS)}</b>
                                      </p>
                                            <BaseGridXL
                                              height="300px"
                                              keyName="rowid"
                                              columnDefs={this.GetGroupTransgressionsColumns}
                                              rowData={finalGroupTransgressionList}
                                              loading={this.notReadyYet()}
                                              onRowDoubleClicked={this.onPharmacyIdSelected}
                                              exportName="PharmagardeGroupTransgressions"
                                            />
                                    </DivColumn>
                                  </DivLine>
                                </DivColumn>  */}
                              </DivLine>
                            </span>
                          }
                        />
                        <Tab
                          id={SUBTAB.SPECIALISSUES}
                          title={this.props.translate(TLCode.SPECIALISSUES_TITLE)}
                          panel={
                            <span>
                              <DivLine>
                                <DivColumn style={{ width: '600px' }}>
                                  <p>
                                    <b>{this.props.translate(TLCode.SPECIALISSUES_BLACKPOINTTRANSGRESSIONS)}</b>
                                  </p>
                                  <BaseGridXL
                                    height="300px"
                                    keyName="rowid"
                                    columnDefs={this.GetBlackpointTransgressionsColumns}
                                    rowData={finalBlackpointTransgressionList}
                                    loading={this.notReadyYet()}
                                    onRowDoubleClicked={this.onPharmacyIdSelected}
                                    exportName="PharmagardeBlackpointTransgressions"
                                  />
                                </DivColumn>
                              </DivLine>
                            </span>
                          }
                        />
                        <Tab
                          disabled={true}
                          id={SUBTAB.INSUFFICIENTGEOCOVERAGE}
                          title={this.props.translate(TLCode.INSUFICIENT_GEO_COVERAGE)}
                          panel={
                            <span>
                              <p>
                                <b>{this.props.translate(TLCode.FAKE)}</b>
                              </p>
                              <p>
                                <b>Périodes de garde avec couverture géographique insuffisante</b>
                                <br />
                                <b>Indice de performance : 85.02%</b>&nbsp;&nbsp;&nbsp;
                                {/* </p>
                              <p> */}
                                1.11% - 4 sur un total de 364 périodes de garde
                              </p>
                              <p>{this.props.translate(TLCode.PHARMACY_LINKS_NOTICE)}</p>
                              <BaseGridXL
                                height="600px"
                                keyName="scoreId"
                                columnDefs={this.scoreColumnDefs}
                                rowData={scoreList}
                                loading={this.notReadyYet()}
                                onRowDoubleClicked={this.onScoreSelected}
                              />
                            </span>
                          }
                        />
                      </Tabs>
                    </span>
                  }
                />
                <Tab
                  id={TAB.SCORES}
                  title={this.props.translate(TLCode.ANALYSIS_SCORES)}
                  panel={
                    <span>
                      {/* <p>{this.props.translate(TLCode.SCORE_LINKS_NOTICE)}</p> */}

                      <BaseGridXL
                        height="600px"
                        keyName="scoreId"
                        columnDefs={this.scoreColumnDefs}
                        rowData={scoreList}
                        loading={this.notReadyYet()}
                        onRowDoubleClicked={this.onScoreSelected}
                        exportName="PharmagardeScores"
                      />
                    </span>
                  }
                />
                <Tab
                  id={TAB.MATCHES}
                  title={this.props.translate(TLCode.ANALYSIS_MATCHES)}
                  panel={
                    <span>
                      <span>
                        <span>
                          <DivLine>
                            {previousScore ? (
                              <Tooltip content={this.getSolutionLabel(previousScore)} position={Position.LEFT}>
                                <Button
                                  icon={IconNames.CHEVRON_LEFT}
                                  large={true}
                                  style={{ marginLeft: '2px' }}
                                  onClick={() => this.goPrevious()}
                                />
                              </Tooltip>
                            ) : (
                              <span />
                            )}
                            <DivBox>
                              <DivLine>
                                {this.props.translate(TLCode.ANALYSIS_MATCHES_CURR)}
                                {this.getSolutionLabel(currentScore)}
                              </DivLine>
                            </DivBox>
                            {nextScore ? (
                              <Tooltip content={this.getSolutionLabel(nextScore)} position={Position.RIGHT}>
                                <Button
                                  icon={IconNames.CHEVRON_RIGHT}
                                  large={true}
                                  style={{ marginLeft: '2px' }}
                                  onClick={() => this.goNext()}
                                />
                              </Tooltip>
                            ) : (
                              <span />
                            )}
                          </DivLine>
                        </span>
                        <span>
                          <BaseGridXL
                            height="600px"
                            keyName="constraintMatchId"
                            columnDefs={this.solutionDetailColumns}
                            rowData={matchList}
                            loading={this.notReadyYet()}
                            onRowDoubleClicked={this.onRuleSelected}
                            exportName="PharmagardeMatches"
                          />
                        </span>
                      </span>
                    </span>
                  }
                />
                <Tab
                  id={TAB.TRANSGRESSIONS}
                  title={this.props.translate(TLCode.ANALYSIS_TRANSGRESSIONS)}
                  panel={
                    <span>
                      <DivLine>
                        {previousScore ? (
                          <Tooltip content={this.getSolutionLabel(previousScore)} position={Position.LEFT}>
                            <Button
                              icon={IconNames.CHEVRON_LEFT}
                              large={true}
                              style={{ marginLeft: '2px' }}
                              onClick={() => this.goPrevious()}
                            />
                          </Tooltip>
                        ) : (
                          <span />
                        )}
                        {/* <DivBox>
                          {this.props.translate(TLCode.ANALYSIS_MATCHES_CURR)}
                          {this.getSolutionLabel(currentScore)}
                        </DivBox> */}
                        <DivBox>
                          <DivLine>
                            {this.props.translate(TLCode.ANALYSIS_MATCHES_CURR)}
                            {this.getSolutionLabel(currentScore)}
                          </DivLine>
                        </DivBox>
                        {nextScore ? (
                          <Tooltip content={this.getSolutionLabel(nextScore)} position={Position.RIGHT}>
                            <Button
                              icon={IconNames.CHEVRON_RIGHT}
                              large={true}
                              style={{ marginLeft: '2px' }}
                              onClick={() => this.goNext()}
                            />
                          </Tooltip>
                        ) : (
                          <span />
                        )}
                      </DivLine>
                      <span>
                        <DivLine>
                          <InputGroup
                            placeholder="Filtrer les deux grilles"
                            leftIcon="search"
                            value={this.state.ruleFilter}
                            onChange={(e) => this.setFilterText(e.target.value)}
                          />
                          <p>{this.props.translate(TLCode.PHARMACY_FILTER_NOTICE)}</p>
                          <p>{this.props.translate(TLCode.PHARMACY_LINKS2_NOTICE)}</p>
                        </DivLine>
                        <ObservationGridContainer>
                          <HalfContainer>
                            <p>
                              <b>{'Indictments'}</b>
                            </p>
                            <BaseGrid
                              height="600px"
                              keyName="apbNumber"
                              columnDefs={this.indictmentCountColumnDefs}
                              rowData={indictmentCountList}
                              loading={this.notReadyYet()}
                              onRowDoubleClicked={this.onApbNrSelected2}
                              quickFilterText={this.state.ruleFilter}
                            />
                          </HalfContainer>
                          <HalfContainer>
                            <BaseGrid
                              height="600px"
                              keyName="indictmentId"
                              columnDefs={this.indictmentColumnDefs}
                              rowData={indictmentList}
                              loading={this.notReadyYet()}
                              onRowDoubleClicked={this.onApbNrSelected}
                              quickFilterText={this.state.ruleFilter}
                            />
                          </HalfContainer>
                        </ObservationGridContainer>
                        <ObservationGridContainer>
                          {/* <DivLine>
                            <DivColumn > */}
                          <HalfContainer>
                            <p>
                              <b>{this.props.translate(TLCode.SPECIALISSUES_GROUPTRANSGRESSIONS)}</b>
                            </p>
                            <BaseGridXL
                              height="600px"
                              keyName="rowid"
                              columnDefs={this.GetGroupTransgressionsColumns}
                              rowData={finalGroupTransgressionList}
                              loading={this.notReadyYet()}
                              onRowDoubleClicked={this.onPharmacyIdSelected}
                              exportName="PharmagardeGroupTransgressions"
                            />
                          </HalfContainer>
                          <HalfContainer>
                            <p>
                              <b>{this.props.translate(TLCode.SPECIALISSUES_GROUPTRANSGRESSIONS)}</b>
                            </p>
                            <BaseGridXL
                              height="600px"
                              keyName="rowid"
                              columnDefs={this.GetGroupTransgressionsByDateColumns}
                              rowData={finalGroupTransgressionByDateList}
                              loading={this.notReadyYet()}
                              onRowDoubleClicked={this.onPharmacyIdSelected}
                              exportName="PharmagardeGroupTransgressions"
                            />
                          </HalfContainer>
                          {/* </DivColumn>
                          </DivLine> */}
                        </ObservationGridContainer>
                      </span>
                    </span>
                  }
                />
                <Tab
                  id={TAB.RULES}
                  title={this.props.translate(TLCode.ANALYSIS_RULES)}
                  panel={
                    <span>
                      <DivLine style={{ whiteSpace: 'pre-line' }}>
                        <div>{this.props.PlannerStore!.rules}</div>
                      </DivLine>
                    </span>
                  }
                />
              </Tabs>
            </Card>
          </Container>
        )}
      </MainSectionLayout>
    );
  }
}

export default withTranslate(AnalysisPage);

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const ObservationGridContainer = styled.div({
  marginTop: '5px',
  display: 'flex',
  flexDirection: 'row',
  height: '45%',
  justifyContent: 'space-between',
});

const HalfContainer = styled.div({
  flex: 0.49,
});

const CardStyle = css({
  height: '100%',
  '& .bp3-tabs': {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  '& .bp3-tab-panel': {
    flex: 1,
  },
});

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const DivBox = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  margin-right: 5%;
  border: black;
  border-style: solid;
  border-width: 1px;
  padding: 5px;
`;
